import './checkout.css';

import EventEmitter from "eventemitter3";
import { Data } from '../../../models/data';
import { Page } from '../page';
import { Order } from '../../../models/order';
import { DataLayerService } from '../../../services/data-layer-service';
import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { ProfileService } from '../../../services/profile-service';
import { User } from '../../../models/user';
import { Util } from '../../../utils/util';
import { ContactMask } from '../../../utils/contact-mask';
import { awaitNativeOnce } from '../../../utils/native-bridge';
import { OrderPriceSummary } from '../../shared/OrderPriceSummary/order-price-summary';
import { EventTypes } from '../../../core/event-bus';
import {
    CardOnFileCvvOption, CreditCardAvsOption, CreditCardOption, Curbside, FieldType, MerchantProcessor, Modals, Pages, OnlinePaymentOption, OrderType,
    PaymentType, TimeSelection, PaymentPaymentType, CheckoutMethodType, CardBrand, OrderNotificationType, RoundingRule,
    LoyaltyProvider, WalletType
} from '../../../types/enums';
import { OrderTypeSelector } from '../../shared/OrderTypeSelector/order-type-selector';
import { ICheckout, ICheckoutModules, IGetNotificationSettings } from './checkout.interface';
import { TwoWayBindingHelper } from '../../../utils/two-way-binding';
import { PillBar } from '../../shared/PillBar/pill-bar';
import { IPillBarData } from '../../shared/PillBar/pill-bar.interface';
import { INewName } from '../../../interfaces/global.interfaces';
import { IDataItem, IDataMerchant, IPaymentOptions } from '../../../models/data.interface';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { DialogCreators } from '../../../utils/dialog-creators';
import { isAddressBlockedAsync, showBlockedAddressError } from '../../shared/DeliveryAddress/address-exception';
import { IOrderItem, IOrderItemCoupon, IOrderPayment } from '../../../models/order.interface';
import { OnlineOrderingService } from '../../../services/online-ordering-service';
import { IServiceSaveOrder } from '../../../services/online-ordering-service.interface';
import {
    ICreateMakeList, IMake, InOrder, InOrderAddress, InOrderChallenge, InOrderComment, InOrderCoupon, InOrderCustomer, InOrderCustomerProfile,
    InOrderDonation, InOrderItem, InOrderItemComment, InOrderItemInfo, InOrderItemModifier, InOrderItemTax, InOrderMakeList, InOrderServiceCharge, ITax
} from '../../../online-ordering/interfaces/online-ordering.interface';
import { PrivacyTerms } from '../../shared/PrivacyTerms/privacy-and-terms';
import { IUserCard } from '../../../models/user.interface';
import { TimeSection } from '../../shared/TimeSection/time-section';
import { LoaderSpinner } from '../../shared/LoaderSpinner/loader-spinner';
import { Donation } from './components/donation/donation';
import { LocationSection } from './components/location-section/location-section';
import { OrderDetailsSection } from './components/order-details-section/order-details-section';
import { Router } from '../../../core/router'
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { InputValidator } from '../../../validators/input-validator';
import { IValidationField } from '../../../validators/validation-field.interface';
import { PaymentSectionGiftCard } from './components/payment-section/payment-section-gift-card';
import { PaymentOptionGroup } from './components/payment-section/payment-option-group';
import { IPaymentOptionElement } from './components/payment-section/payment-option-row';

export class Checkout extends Page {
    private readonly _order: Order;
    private readonly _user: User;
    private readonly _dataLayer: DataLayerService;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _profileService: ProfileService;
    private readonly _onlineOrderingService: OnlineOrderingService;
    private readonly _bindingHelper: TwoWayBindingHelper<ICheckout>;
    private _item: ICheckout;
    private readonly _modules: ICheckoutModules;
    private _hpsWired = false;
    private _hlDebounceId?: number;
    private _areWalletsHiddenForFutureDate = false;
    private static readonly FEROOT_SCRIPT_ID = 'feroot-checkout-script';
    private static readonly FEROOT_SCRIPT_URL = 'https://pg.feroot.com/v1/bundle/e7aed066-05a4-4073-9a6b-f28154ed8faf';

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, user: User, dataLayer: DataLayerService, loyaltyProvider: ILoyaltyProvider, onlineOrderingService: OnlineOrderingService, profileService: ProfileService) {
        super("page_checkout", eventBus, data, context, localizationService);
        this._item = this._createModel();
        this._order = order;
        this._user = user;
        this._dataLayer = dataLayer;
        this._loyaltyProvider = loyaltyProvider;
        this._profileService = profileService;
        this._onlineOrderingService = onlineOrderingService;
        this._modules = this._createModules();
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._eventBusListeners();
        this._setTwoWayListeners();
    }

    private _eventBusListeners() {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._orderTypeChanged);
        this._eventBus.on(EventTypes.STORE_CHANGED, this._storeChanged);
        this._eventBus.on(EventTypes.CART_CHANGED, this._cartChanged);
        this._eventBus.on(EventTypes.LOGGED_IN, this._updateLoggedIn);
        this._eventBus.on(EventTypes.LOGGED_OUT, this._updateLoggedIn);
        this._eventBus.on(EventTypes.CHECKOUT_CONTACTLESS, this._contactlessOrCurbsideOnClick);
        this._eventBus.on(EventTypes.TIME_CHANGED, this._orderTimeChanged);
        this._eventBus.on(EventTypes.TIME_CHANGED, this._refreshWalletsForOrderDate);
        this._eventBus.on(EventTypes.SCHEDULE_DATE_CHANGED, this._refreshWalletsForOrderDate);
        this._eventBus.on(EventTypes.ORDER_COMPLETED, this._orderCompleted)
    }

    private _createModel(): ICheckout {
        return {
            isSubmitting: false,
            isPageShow: false,
            customTip: "0.00",
            paymentOptionId: null,
            methodId: CheckoutMethodType.IN_STORE,
            paymentOptions: null,
            toGoItems: [],
            comments: "",
            termsAndConditions: false,
            contactInfoFirstName: "",
            contactInfoLastName: "",
            contactInfoCountryCodeId: 1,
            contactInfoPhone: "",
            contactInfoEmail: "",
            creditCardInfoCardNumber: "",
            creditCardInfoExpirationDate: "",
            creditCardInfoCvv: "",
            creditCardInfoAddress: "",
            creditCardInfoZip: "",
            creditCardInfoSaveToken: true,
            curbsideInfoIsCurbside: false,
            curbsideInfoCarText: "",
            notificationInfoIsNotifyPhone: true,
            notificationInfoIsNotifyEmail: true,
            notificationInfoCountryCodeId: 1,
            notificationInfoPhone: "",
            notificationInfoEmail: "",
            customerWalletCard: null,
            applePayToken: null,
            googlePayToken: null
        };
    }

    private _createModules(): ICheckoutModules {
        const giftCard = new PaymentSectionGiftCard(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            this._order,
            this._dataLayer,
            this._onlineOrderingService,
            {
                getIsCurbside: this.getIsCurbside,
                getPaymentOption: this.getPaymentOption,
                setPaymentOption: this.setPaymentOption
            }
        );

        // OrderHub-only; built up front so its item cards receive DATA_RETRIEVED to pick a UI mode
        const orderDetailsSection = this._context.getProperty("isOrderHub")
            ? new OrderDetailsSection({
                eventBus: this._eventBus,
                data: this._data,
                context: this._context,
                localizationService: this._localizationService,
                order: this._order,
                dataLayerService: this._dataLayer,
                loyaltyProvider: this._loyaltyProvider,
                profileService: this._profileService
            })
            : null;

        return {
            locationSection: new LocationSection(this._eventBus, this._data, this._context, this._localizationService, this._order),
            orderDetailsSection,
            timeSection: new TimeSection(this._eventBus, this._data, this._context, this._localizationService, this._order, this._onlineOrderingService, this._dataLayer, "checkout", this._onTimeSectionChange),
            donation: new Donation(this._data, this._context, this._localizationService, this._order),
            giftCard
        };
    }

    private _resetItemModel(): void {
        const tempItem = this._createModel();
        this._item.isSubmitting = tempItem.isSubmitting;
        this._item.customTip = tempItem.customTip;
        this._item.paymentOptionId = tempItem.paymentOptionId;
        this._item.methodId = tempItem.methodId;
        this._item.paymentOptions = tempItem.paymentOptions;
        this._item.toGoItems = tempItem.toGoItems;
        this._item.comments = tempItem.comments;
        this._item.termsAndConditions = tempItem.termsAndConditions;
        this._item.contactInfoFirstName = tempItem.contactInfoFirstName;
        this._item.contactInfoLastName = tempItem.contactInfoLastName;
        this._item.contactInfoCountryCodeId = tempItem.contactInfoCountryCodeId;
        this._item.contactInfoPhone = tempItem.contactInfoPhone;
        this._item.contactInfoEmail = tempItem.contactInfoEmail;
        this._item.creditCardInfoCardNumber = tempItem.creditCardInfoCardNumber;
        this._item.creditCardInfoExpirationDate = tempItem.creditCardInfoExpirationDate;
        this._item.creditCardInfoCvv = tempItem.creditCardInfoCvv;
        this._item.creditCardInfoAddress = tempItem.creditCardInfoAddress;
        this._item.creditCardInfoZip = tempItem.creditCardInfoZip;
        this._item.curbsideInfoIsCurbside = tempItem.curbsideInfoIsCurbside;
        this._item.curbsideInfoCarText = tempItem.curbsideInfoCarText;
        this._item.notificationInfoIsNotifyPhone = tempItem.notificationInfoIsNotifyPhone;
        this._item.notificationInfoIsNotifyEmail = tempItem.notificationInfoIsNotifyEmail;
        this._item.notificationInfoCountryCodeId = tempItem.notificationInfoCountryCodeId;
        this._item.notificationInfoPhone = tempItem.notificationInfoPhone;
        this._item.notificationInfoEmail = tempItem.notificationInfoEmail;
        this._item.customerWalletCard = tempItem.customerWalletCard;
        this._item.applePayToken = tempItem.applePayToken;
        this._item.googlePayToken = tempItem.googlePayToken;
        this._modules.giftCard.resetModel();
    }

    public async initAsync(): Promise<void> {
        this._setEventListeners();
    }

    private readonly getIsCurbside = (): boolean => {
        return this._item.curbsideInfoIsCurbside;
    }

    private readonly getPaymentOption = (): OnlinePaymentOption | null => {
        return this._item.paymentOptionId;
    }

    private readonly setPaymentOption = (option: OnlinePaymentOption | null): void => {
        this._item.paymentOptionId = option;
    }
        
    private debounce(fn: () => void, ms = 100) {
        if (this._hlDebounceId) window.clearTimeout(this._hlDebounceId);
        this._hlDebounceId = window.setTimeout(fn, ms);
    }

    // Check containers exist
    private heartlandTargetsExist(): boolean {
        return !!(document.getElementById("iframesCardNumber")
            && document.getElementById("iframesCardExpiration")
            && document.getElementById("iframesCardCvv"));
    }

    // Safe mount trigger
    private scheduleHeartlandMount() {
        if (this._isHeartland())
            return;
        this.debounce(() => {
            const arm = () => {
                if (!this.heartlandTargetsExist()) {
                    requestAnimationFrame(arm); // try next paint until containers present
                    return;
                }
                this.setHeartland();
            };
            arm();
        }, 100);
    }


    public override show(): void {
        if (!this._data.storeKey || this._order.getProperty("Items").length === 0) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LANDING);
            return;
        }

        this._loadFerootScript();

        this._item.isPageShow = false;
        super.show();

        this._startPageAsync();
        this._headerTotalUpdate();

        if (this._dataLayer) {
            this._dataLayer.begin_checkout(this._order);
            this._dataLayer.add_shipping_info(this._order, this._item.curbsideInfoIsCurbside);
            this._dataLayer.page_view(this._profileService.user.isLoggedIn(), this._data.getProperty("StoreInfo").storeCode.toString(), this._profileService.user.user);
        }
    }

    public override hide(): void {
        this._removeFerootScript();
        super.hide();
    }

    private _loadFerootScript(): void {
        if (document.getElementById(Checkout.FEROOT_SCRIPT_ID))
            return;

        const script = document.createElement('script');
        script.id = Checkout.FEROOT_SCRIPT_ID;
        script.src = Checkout.FEROOT_SCRIPT_URL;
        script.type = 'text/javascript';
        document.head.appendChild(script);
    }

    private _removeFerootScript(): void {
        const script = document.getElementById(Checkout.FEROOT_SCRIPT_ID);
        if (script)
            script.remove();
    }

    private async _startPageAsync(): Promise<void> {
        const success = await this._loyaltyProvider.batchComparison(true);
        if (!success)
            return;

        // Reset Payment and Place Order button
        this._item.paymentOptionId = null;
        Util.hideElement("div_checkout_credit_card_expansion");
        Util.showElement("btn_checkout_place_order");
        Util.showElement("btn_checkout_place_order_mobile");
        Util.hideElement("btn_checkout_apple_pay_bottom");
        Util.hideElement("btn_checkout_apple_pay_bottom_mobile");
        Util.hideElement("div_checkout_google_pay_bottom");
        Util.hideElement("div_checkout_google_pay_bottom_mobile");

        this._renderPage(true);
        this._setEventListeners();
        super.show();
        this._item.isPageShow = true;
    }

    private _renderPage(isFirstTime?: boolean): void {
        if (!this._data.storeKey)
            return;

        this._showOrderNumber();
        this._createOrderTypes(isFirstTime);
        this._renderLocationInfo();
        this._renderMethodsSection();
        this._renderTimesSection();
        this._renderCustomerInfoSection();
        this._renderToGoItemsSection();
        this._renderNotificationsSection();
        this._renderCommentsSection();
        this._renderDonationsSection();

        this._modules.giftCard.renderGiftCards();
        this._renderPaymentMethodsSection(isFirstTime);
        this._renderTipsSection();
        this._createOrderSummary();
        this._createFinalSection();
        //if (this._loyaltyProvider.getLoyaltyProvider() !== LoyaltyProvider.PUNCHH)
        //    Util.hideElement("btn_checkout_import_digital_gift_card")
        // 2DO
        //this._setRedeemCurrencyGUI();
        //this._GUI_CHKOUT_CC_Exp_Year();

        // 2DO
        //Util.hideElement("lbl_checkout_credit_expect_call");

        // 2DO: Show & Hide Contactless Delivery & Billinag Address Same as Delivery Address Checkbox
        //const payOptions = this._getPaymentOptionSettings();
        //if (this._order.getProperty("OrderTypeID") === OrderType.DELIVERY && payOptions?.CreditCardAvsOption !== CreditCardAvsOption.DO_NOT_VALIDATE) {
        //    Util.showElement("div_checkout_delivery");
        //    Util.showElement("lbl_checkout_credit_address_chk");
        //    Util.showElement("div_checkout_billing_add");
        //} else {
        //    Util.hideElement("div_checkout_delivery");
        //    Util.hideElement("lbl_checkout_credit_address_chk");
        //    Util.hideElement("div_checkout_billing_add");
        //}
    }

    private _setEventListeners(): void {
        Util.setElement("onclick", "btn_checkout_contact_update", this._updateContactInfoOnClick);
        Util.setElement("onclick", "btn_checkout_place_order", this._placeOrderOnClickAsync);
        Util.setElement("onclick", "btn_checkout_place_order_mobile", this._placeOrderOnClickAsync);
        Util.setElement("onclick", "chk_checkout_notification_text", () => { setTimeout(this._notificationPhoneOnClick, 0); });
        Util.setElement("onclick", "chk_checkout_notification_email", () => { setTimeout(this._notificationEmailOnClick, 10); });
        Util.setElement("onclick", "div_checkout_payment_existing_new", this._addNewCardOnClick);
        Util.addKeyboardActivation(document.getElementById("div_checkout_payment_existing_new"));
        Util.setElement("onclick", "btn_checkout_apple_pay_bottom", this._placeOrderOnClickAsync);
        Util.setElement("onclick", "btn_checkout_apple_pay_bottom_mobile", this._placeOrderOnClickAsync);
        Util.setElement("onclick", "div_checkout_privacy_terms", this._onPrivacyTermsLinkClick, true);
        this._modules.giftCard.setupEventListeners();

        this._loadGooglePayScript();

        const countries = this._data.getProperty("Countries");
        Util.populateCountryCodeSelectList("dl_checkout_country_code", "txt_checkout_phone", countries, this._user.getProperty("CountryCodeId") || this._data.getProperty("StoreInfo").countryId || 1);
        Util.populateCountryCodeSelectList("dl_checkout_notification_country_code", "txt_checkout_notification_phone", countries, this._user.getProperty("MarketingCountryId") || this._data.getProperty("StoreInfo").countryId || 1);

        const customTipElement = document.getElementById("txt_checkout_tip_custom") as HTMLInputElement | undefined;
        if (customTipElement) {
            Util.setElement("onchange", customTipElement, this._customTipOnChange);
            Util.setElement("onfocus", customTipElement, this._customTipOnFocus);
            Util.setElement("oninput", customTipElement, () => {
                Util.attachNumericInputValidation(customTipElement, { min: 0, max: 10000, decimals: 2 })
                this._item.customTip = customTipElement.value;
            });
        }

        Util.setElement("oninput", "txt_checkout_payment_card_number", this._onCreditCardNumberInput);
        if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants"))) {
            Util.setElement("oninput", "txt_checkout_op_payment_card_number", this._onCreditCardNumberInput);
        }
        if (!this._isHeartland()) {
            document.getElementById("txt_checkout_payment_expiration_date")!.oninput = (event) => this._onCreditCardExpirationInput(event as InputEvent);
        } 
    }

    private _setTwoWayListeners(): void {
        this._bindingHelper.bindToInput("contactInfoFirstName", "txt_checkout_first_name");
        this._bindingHelper.bindToInput("contactInfoLastName", "txt_checkout_last_name");
        this._bindingHelper.bindToInput("contactInfoCountryCodeId", "dl_checkout_country_code");
        this._bindingHelper.bindToInput("contactInfoPhone", "txt_checkout_phone");
        this._bindingHelper.bindToInput("contactInfoEmail", "txt_checkout_email");
        this._bindingHelper.bindToInput("customTip", "txt_checkout_tip_custom");
        this._bindingHelper.bindToInput("creditCardInfoCardNumber", "txt_checkout_payment_card_number");
        this._bindingHelper.bindToInput("creditCardInfoCardNumber", "txt_checkout_op_payment_card_number");
        this._bindingHelper.bindToInput("creditCardInfoExpirationDate", "txt_checkout_payment_expiration_date");
        this._bindingHelper.bindToInput("creditCardInfoCvv", "txt_checkout_payment_cvv");
        this._bindingHelper.bindToInput("creditCardInfoAddress", "txt_checkout_payment_address");
        this._bindingHelper.bindToInput("creditCardInfoZip", "txt_checkout_payment_zip_code");
        this._bindingHelper.bindToInput("creditCardInfoSaveToken", "chk_checkout_payment_token");
        this._bindingHelper.bindToInput("curbsideInfoCarText", "txt_checkout_method_curbside_car_info");
        this._bindingHelper.bindToInput("comments", "txt_checkout_comment");
        this._bindingHelper.bindToInput("notificationInfoIsNotifyPhone", "chk_checkout_notification_text");
        this._bindingHelper.bindToInput("notificationInfoIsNotifyEmail", "chk_checkout_notification_email");
        this._bindingHelper.bindToInput("notificationInfoCountryCodeId", "dl_checkout_notification_country_code");
        this._bindingHelper.bindToInput("notificationInfoPhone", "txt_checkout_notification_phone");
        this._bindingHelper.bindToInput("notificationInfoEmail", "txt_checkout_notification_email");
        this._modules.giftCard.setupTwoWayListeners();
    }

    private readonly _placeOrderOnClickAsync = async (): Promise<void> => {
        if (this._order.getProperty("Items").length === 0) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names(
                    "Error_NoItemsHaveBeenAddedToOrder"
                ),
                true
            );
            return;
        }

        // Apple Pay: session.begin() must be called synchronously before any await
        if (this._item.paymentOptionId === OnlinePaymentOption.APPLE_PAY && !this._item.applePayToken) {
            this._initiateApplePay();
            return;
        }

        // Google Pay: loadPaymentData() must be called from a user gesture
        if (this._item.paymentOptionId === OnlinePaymentOption.GOOGLE_PAY && !this._item.googlePayToken) {
            void this._initiateGooglePayAsync();
            return;
        }

        if (!await this._checkMinimumDeliveryOrderAsync())
            return;

        if (!await this._checkDeliveryAddressExceptionAsync())
            return;

        if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants")))
            await this._openPaySubmitAsync();
        else
            await this._submitAsync(false);
    }

    private readonly _onTimeSectionChange = (type: TimeSelection, futureDate?: Date, futureMinute?: number): void => {
        if (type === TimeSelection.ASAP)
            this._order.setAsapTime();
        else if (futureDate && futureMinute !== undefined)
            this._order.setFutureDate(futureDate, futureMinute);
    }

    private readonly _orderTypeChanged = (): void => {
        if (Router.path !== Pages.CHECKOUT)
            return;

        this._renderPage(false);

        if (this._dataLayer) {
            this._dataLayer.add_shipping_info(this._order, this._item.curbsideInfoIsCurbside);
        }
    }

    private readonly _storeChanged = (): void => {
        this._resetItemModel();
        this.scheduleHeartlandMount();
        this._modules.timeSection.resetItemModel();
    }

    private readonly _cartChanged = (): void => {
        if (Router.path !== Pages.CHECKOUT)
            return;

        this._renderPaymentMethodsSection();
        this._createOrderSummary();
        this._headerTotalUpdate();
    }

    private readonly _orderTimeChanged = async (): Promise<void> => {
        if (this._order.getProperty("Coupons").length > 0 &&
            ((this._order.getProperty("FutureDate") && this._order.getProperty("FutureMinute")) ||
            (this._order.getProperty("FutureDate") === null && this._order.getProperty("FutureMinute") === -1))
        ) {
            await this._order.removeAndReapplyAllCouponsAsync(this._loyaltyProvider, true);

            if (this._dataLayer) {
                this._dataLayer.add_shipping_info(this._order, this._item.curbsideInfoIsCurbside);
            }
        }
    }

    private readonly _orderCompleted = (): void => {
        this._resetItemModel();
        this._modules.timeSection.resetItemModel();
        this._modules.orderDetailsSection?.collapse();
    }

    private readonly _updateLoggedIn = (): void => {
        this._renderCustomerInfoSection();
        this._renderNotificationsSection();
        if (this._user.isLoggedIn()) {
            Util.showElement("div_checkout_payment_token")
        } else {
            Util.hideElement("div_checkout_payment_token")
        }

        // Whether the wallet/saved-cards option is available depends on being logged in, so the
        // payment options need rebuilding here too -- otherwise signing in mid-checkout leaves the
        // saved cards hidden until the user leaves and re-enters the page.
        this._renderPaymentMethodsSection();

        this._createFinalSection();
    }

    private _getPaymentOptionSettings(): IPaymentOptions | null {
        return this._data.getProperty("Settings").OPO.find(x => x.OrderTypeID == this._order.getProperty("OrderTypeID")) || null;
    }

    private _showOrderNumber(): void {
        return;

        // Skip For now
        // const showFunction = this._context.getProperty("isOrderHub") ? Util.showElement : Util.hideElement;
        // showFunction("div_checkout_order_number");

        // const orderNumberSpan = document.getElementById("spn_checkout_order_number");
        // if (orderNumberSpan)
        //     orderNumberSpan.innerText = this._order.getProperty("OrderNo").toString();
    }

    private _createOrderTypes(isFirstTime: boolean = true): void {
        if (!isFirstTime) return;

        const parentDiv = document.getElementById("div_checkout_order_types");
        if (!parentDiv)
            return;

        if (this._context.getProperty("isOrderHub")) {
            parentDiv.remove();
            return;
        }

        parentDiv.innerHTML = "";

        const orderSelector = new OrderTypeSelector(
            this._eventBus,
            this._data,
            this._context,
            this._localizationService,
            this._dataLayer,
            this._order,
            this._loyaltyProvider,
            this._data.getProperty("OrderTypeSubTypes"),
            this._order.getProperty("OrderTypeID"),
        );
        parentDiv.appendChild(orderSelector.element);
    }

    private _renderLocationInfo(): void {
        const parentDiv = document.getElementById("div_checkout_location_section");
        if (!parentDiv)
            return;

        parentDiv.innerHTML = "";

        this._modules.locationSection.init();
        parentDiv.appendChild(this._modules.locationSection.element);

        if (this._modules.orderDetailsSection) {
            this._modules.orderDetailsSection.init();
            parentDiv.appendChild(this._modules.orderDetailsSection.element);
        }
    }

    private readonly _contactlessOrCurbsideOnClick = (): void => {
        if (this._order.getProperty("OrderTypeID") === OrderType.DELIVERY) {
            this._item.curbsideInfoIsCurbside = false;
        } else {
            this._modules.locationSection.contactless = false;
        }

        this._renderPaymentMethodsSection();

        if (this._dataLayer) {
            this._dataLayer.add_shipping_info(this._order, this._item.curbsideInfoIsCurbside);
        }
    }

    private _renderMethodsSection(): void {
        Util.hideElement("div_checkout_method_section");

        // Reset Curbside status if order type not take out
        if (this._order.getProperty("OrderTypeID") !== OrderType.TAKE_OUT) {
            this._item.curbsideInfoIsCurbside = false;
        }

        const methods = this._getDifferentMethods();
        if (methods.length == 0)
            return;

        this._createMethodsHeaderText();

        const parentDiv = document.getElementById("div_checkout_method_options");
        if (!parentDiv)
            return;

        parentDiv.innerHTML = "";

        const data: IPillBarData = {
            buttonId: "checkout_methods",
            ariaLabelledBy: "p_checkout_method_header",
            buttons: []
        };

        // In-Store Option
        data.buttons.push({
            id: CheckoutMethodType.IN_STORE.toString(),
            text: this._localizationService.names("InStore"),
            selected: this._item.methodId === CheckoutMethodType.IN_STORE,
            ltag: "InStore",
            function: () => { this._methodOnClick(CheckoutMethodType.IN_STORE); }
        });

        // Set Additional Method Options
        for (const method of methods) {
            data.buttons.push({
                id: method.type.toString(),
                text: this._localizationService.names(method.name),
                selected: this._item.methodId === method.type,
                ltag: method.name,
                function: () => { this._methodOnClick(method.type); }
            });
        }

        const pillBar = new PillBar(data, this._localizationService);
        parentDiv.appendChild(pillBar.element);
        pillBar.updateBeginningArrows();
        Util.showElement("div_checkout_method_section");
    }

    private _createMethodsHeaderText(): void {
        const orderTypeSubType = this._data.getProperty("OrderTypeSubTypes").find(x => x.OrderTypeId == this._order.getProperty("OrderTypeID"));
        if (!orderTypeSubType)
            return;

        const orderTypeName = this._localizationService.GetName(orderTypeSubType.Names);
        const languages = this._localizationService.getSupportedLanguages();
        const nameObj: INewName[] = languages.map(lang => {
            const tempText = this._localizationService
                .names("CarryoutMethod", lang)
                .replace(
                    "{{order_type_name}}",
                    this._localizationService.GetName(orderTypeSubType.Names, lang),
                );
            return { lc: lang, text: tempText };
        });

        Util.setElement("ltagj", "p_checkout_method_header", Util.toLtagj(nameObj));
        Util.setElement("innerText", "p_checkout_method_header", this._localizationService.names("CarryoutMethod").replace("{{order_type_name}}", orderTypeName));
    }

    private readonly _getDifferentMethods = (): { type: CheckoutMethodType, name: string }[] => {
        const orderTypeId = this._order.getProperty("OrderTypeID");
        const methods: { type: CheckoutMethodType, name: string }[] = [];
        if (orderTypeId === OrderType.TAKE_OUT) {
            if (this._data.getProperty("Settings").CURBSD > 0)
                methods.push({ type: CheckoutMethodType.CURBSIDE, name: "Curbside" });
        }

        return methods;
    }

    private readonly _methodOnClick = (methodId: CheckoutMethodType): void => {
        this._item.methodId = methodId;

        Util.hideElement("div_checkout_method_curbside");

        switch (methodId) {
            case CheckoutMethodType.IN_STORE:
                this._item.curbsideInfoIsCurbside = false;
                this._renderPaymentMethodsSection();
                break;
            case CheckoutMethodType.CURBSIDE:
                this._renderCurbsideSection();
                break;
        }
    }

    private readonly _renderCurbsideSection = (): void => {
        this._item.curbsideInfoIsCurbside = true;

        const curbsideMessage = this._data.getProperty("Settings").OOS.curbsideMessage;
        if (curbsideMessage) {
            Util.setElement("innerText", "p_checkout_method_curbside_info", curbsideMessage);
            Util.showElement("p_checkout_method_curbside_info");
        } else
            Util.hideElement("p_checkout_method_curbside_info");

        if (this._data.getProperty("Settings").CURBSD === Curbside.CUSTOMER_CAR_INFO)
            Util.showElement("div_checkout_method_curbside_car_info");
        else
            Util.hideElement("div_checkout_method_curbside_car_info");

        Util.showElement("div_checkout_method_curbside");
        this._contactlessOrCurbsideOnClick();
    }

    private _renderTimesSection(): void {
        const parent = document.getElementById("div_checkout_time_section");
        if (!parent)
            return;

        if (this._context.getProperty("isOrderHub")) {
            parent.remove();
            return;
        }

        parent.innerHTML = "";
        parent.appendChild(this._modules.timeSection.element);
        this._modules.timeSection.init();
    }

    private _renderCustomerInfoSection(): void {
        if (this._context.getProperty("qrCode"))
            return;

        if (this._context.getProperty("isOrderHub")) {
            document.getElementById("div_checkout_contact_info")?.remove();
            return;
        }

        if (this._user.isLoggedIn()) {
            Util.hideElement("div_checkout_contact_info_non_signed_in");
            Util.showElement("btn_checkout_contact_update");
            Util.showElement("div_checkout_contact_info_signed_in");
            Util.setElement("innerText", "p_checkout_contact_info_signed_in_name", `${this._user.getProperty("Name")} ${this._user.getProperty("LastName")}`);
            this._renderSignedInContactValues();
        } else {
            Util.showElement("div_checkout_contact_info_non_signed_in");
            Util.hideElement("div_checkout_contact_info_signed_in");
            Util.hideElement("btn_checkout_contact_update");
        }
    }

    // Masked whenever Setting 274 MFA is on, the same as Account Details.
    private _renderSignedInContactValues(): void {
        const mask = this._profileService.isMfaEnabled();

        const phoneNumber = this._user.getProperty("Phone") || "";
        const formattedPhoneNumber = Util.formatPhoneNumberByCountryId(phoneNumber, this._data.getProperty("StoreInfo").countryId, this._data.getProperty("Countries"));
        Util.setElement("innerText", "p_checkout_contact_info_signed_in_phone", mask ? ContactMask.phone(formattedPhoneNumber) : formattedPhoneNumber);

        const email = this._user.getProperty("Email") || "";
        Util.setElement("innerText", "p_checkout_contact_info_signed_in_email", mask ? ContactMask.email(email) : email);
    }

    // Same modal as Account Details, and it can change email and phone, so it takes the same MFA gate (#82859).
    private readonly _updateContactInfoOnClick = async (): Promise<void> => {
        if (await this._profileService.ensureAccountMfaAsync())
            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.ACCOUNT_CONTACT, { fromCheckout: true });
    }

    private _renderToGoItemsSection(): void {
        if (this._context.getProperty("isOrderHub")) {
            document.getElementById("div_checkout_togo_supplies_options")?.remove();
            return;
        }

        Util.setElement("innerHTML", "div_checkout_togo_supplies_options", "");
        const togoItems = this._data.getProperty("Settings").OOS.togoSupplies;
        if (togoItems.length > 0 && !this._context.getProperty("qrCode")) {
            this._GUI_CHKOUT_TogoSupplies(togoItems);
            Util.showElement("div_checkout_togo_supplies");
        } else {
            Util.hideElement("div_checkout_togo_supplies");
        }
    }

    private _GUI_CHKOUT_TogoSupplies(togoItems: string[]): void {
        const parentDiv = document.getElementById("div_checkout_togo_supplies_options");
        if (!parentDiv)
            return;

        for (const [index, item] of togoItems.entries()) {
            const div = this._createToGoItemHtml(index, item);
            parentDiv.appendChild(div);
        }
    }

    private _createToGoItemHtml(id: number, item: string): HTMLElement {
        const isSelected = this._item.toGoItems.some(x => x.id === id) ? "checked" : "";
        const html = `
            <label id="lbl_checkout_togo_supplies_option_${id}" class="option-row">
                <span>${item}</span>
                <div class="custom-radio">
                    <input type="checkbox" id="chk_checkout_togo_supplies_option_${id}" ${isSelected}>
                    <span class="checkmark"></span>
                </div>
            </label>`;

        const div = Util.createHtmlElementFromTemplate(html);
        div.onclick = () => this._handleToGoSupplyClick(id, item);

        const checkbox = div.querySelector(`#chk_checkout_togo_supplies_option_${id}`) as HTMLInputElement | null;
        if (checkbox)
            checkbox.onclick = (event: Event) => { event.stopPropagation(); };

        return div;
    }

    private readonly _handleToGoSupplyClick = (id: number, item: string): void => {
        if (this._item.toGoItems.find(x => x.id === id))
            this._item.toGoItems = this._item.toGoItems.filter(x => x.id !== id);
        else
            this._item.toGoItems.push({ id, name: item });
    }

    private _renderCommentsSection(): void {
        if (this._context.getProperty("isOrderHub")) {
            document.getElementById("div_checkout_comment")?.remove();
            return;
        }

        if (this._data.getProperty("Settings").OOC)
            Util.hideElement("div_checkout_comment");
        else
            Util.showElement("div_checkout_comment");
    }

    private _renderTipsSection(): void {
        const settings = this._data.getProperty("Settings");
        if (!settings.OOS.allowGratuity) return;

        Util.hideElement("div_checkout_tip_custom");

        if (this._item.paymentOptionId === OnlinePaymentOption.CASH
            || this._item.paymentOptionId === OnlinePaymentOption.PAY_IN_STORE) {
            this._tipValueOnClick(0);
            Util.hideElement("div_checkout_tip_section");
            return;
        }

        Util.showElement("div_checkout_tip_section");

        const parentDiv = document.getElementById("div_checkout_tip_options");
        if (!parentDiv) return;
        parentDiv.innerHTML = "";

        let sortedTips: number[] = [];
        const tipSettings = settings.OOS.suggestedTips;
        if (tipSettings.length > 0) {
            sortedTips = [...tipSettings];
            sortedTips.sort((a, b) => a - b);
        }

        const orderTotal = Util.Float2(this._order.getProperty("SubTotal") - this._order.getProperty("Discount") + this._order.getProperty("OrderTypeCharge"));
        const currentTip = Util.Float2(this._order.getProperty("Tip") || 0);

        const isPercentMatch = (pct: number) => Util.Float2(orderTotal * (pct / 100)) === currentTip;
        const hasPercentMatch = sortedTips.some(isPercentMatch);
        const isCustomSelected = this._shouldCustomTipBeSelected(currentTip, hasPercentMatch);

        const data: IPillBarData = { buttonId: "checkout_tips", ariaLabelledBy: "p_checkout_tip_header", buttons: [] };

        // No tip
        data.buttons.push({
            id: "0",
            text: this._localizationService.names("NoTip"),
            selected: currentTip === 0,
            ltag: "NoTip",
            function: () => this._tipValueOnClick(0)
        });

        // Percent tips
        let oneSelected = currentTip === 0;
        for (const tip of sortedTips) {
            const selected = !oneSelected && isPercentMatch(tip);
            if (selected) oneSelected = true;
            data.buttons.push({
                id: tip.toString(),
                text: `${tip}%`,
                selected,
                function: () => this._tipValueOnClick(tip)
            });
        }

        // Custom
        data.buttons.push({
            id: "custom",
            text: this._localizationService.names("Custom"),
            selected: isCustomSelected,
            ltag: "Custom",
            function: () => this._tipCustomOnClick()
        });

        const pillBar = new PillBar(data, this._localizationService);
        parentDiv.appendChild(pillBar.element);
        pillBar.updateBeginningArrows();

        if (isCustomSelected) {
            // ensure the input displays the current tip
            this._item.customTip = Util.formatNumber(currentTip);
            Util.showElement("div_checkout_tip_custom");
        }
    }

    private _shouldCustomTipBeSelected(currentTip: number, isPercentMatchSelected: boolean): boolean {
        const orderType = this._order.getProperty("OrderTypeID");
        const isThirdPartyDeliveryAuto = (this._data.getProperty("Settings").TPDM === 1);

        return (currentTip == 0 && isThirdPartyDeliveryAuto && orderType === OrderType.DELIVERY)
            || (currentTip > 0 && !isPercentMatchSelected);
    }

    private readonly _tipValueOnClick = (value: number): void => {
        const orderTotal = Util.Float2(this._order.getProperty("SubTotal") - this._order.getProperty("Discount") + this._order.getProperty("OrderTypeCharge"));
        const tip = Util.Float2(orderTotal * (value / 100));
        this._calculateTip(tip);
        Util.hideElement("div_checkout_tip_custom");
        this._item.customTip = Util.formatNumber(0);
    }

    private readonly _tipCustomOnClick = (): void => {
        Util.showElement("div_checkout_tip_custom");
        this._focusCustomTipInput();
    }

    private _focusCustomTipInput(): void {
        const tipInput = document.getElementById("txt_checkout_tip_custom") as HTMLInputElement;
        if (tipInput && this._item.isPageShow) {
            tipInput.focus();
            tipInput.select();
        }
    }

    private readonly _customTipOnChange = (): void => {
        this._item.customTip = Util.formatNumber(Number(this._item.customTip));
        this._calculateTip(Number(Util.cleanNumber(this._item.customTip, this._data.getProperty("StoreInfo").country)));
    }

    private readonly _customTipOnFocus = (): void => {
        this._item.customTip = Util.cleanNumber(this._item.customTip, this._data.getProperty("StoreInfo").country);
    }

    private _calculateTip(tip: number): void {
        const newTip = Util.Float2(tip);
        const prevTip = Util.Float2(this._order.getProperty("Tip") || 0);

        if (newTip === prevTip) {
            // keep UI consistent, but don’t churn events
            Util.setElement("innerText", "spn_checkout_tip", Util.formatMoney(prevTip, this._data.getProperty("StoreInfo").country));
            return;
        }

        this._order.setProperty("Tip", newTip);
        this._order.calculateTotals();
        Util.setElement("innerText", "spn_checkout_tip", Util.formatMoney(newTip, this._data.getProperty("StoreInfo").country));

        this._eventBus.emit(EventTypes.CART_CHANGED);
        Util.hideElement("spn_checkout_tip_error");
    }

    private _renderDonationsSection(): void {
        const parentDiv = document.getElementById("div_checkout_donation");
        if (!parentDiv)
            return;

        if (this._context.getProperty("isOrderHub")) {
            parentDiv.remove();
            return;
        }

        parentDiv.innerHTML = "";

        const donations = this._data.getProperty("Donations");
        if (donations.length === 0) {
            Util.hideElement(parentDiv);
            return;
        }

        this._modules.donation.init();
        parentDiv.appendChild(this._modules.donation.element);
        Util.showElement(parentDiv);
    }

    private _renderPaymentMethodsSection(isFirstTime: boolean = false): void {
        if (isFirstTime) this._resetPaymentOptionState();
        this._syncDatePickerLock();

        if (this._order.getProperty("AmountDue") === 0) {
            Util.hideElement("div_checkout_payment");
            return;
        }
        Util.showElement("div_checkout_payment");

        const parentDiv = document.getElementById("div_checkout_payment_options_list");
        if (!parentDiv) return;

        const payOptions = this._getPaymentOptionSettings();
        if (!payOptions) return;

        const radioGroupName = "checkout_payments";
        // Build options while the credit-card expansion panel is still attached to the document,
        // since _createCreditCardOption looks up div_checkout_payment_existing_cards (inside the
        // panel) by id to populate the wallet cards.
        const options = this._buildPaymentOptions(payOptions, radioGroupName);
        this._resolveSelectedOption(options);

        // Detach the panel (wallet cards + processor iframes + save-card checkbox) before clearing
        // the list, so the live processor iframes aren't torn down by the innerHTML reset.
        const creditCardExpansionPanel = document.getElementById("div_checkout_credit_card_expansion");
        creditCardExpansionPanel?.remove();

        parentDiv.innerHTML = "";
        this._item.paymentOptions = new PaymentOptionGroup(parentDiv, options);

        if (creditCardExpansionPanel) {
            const creditCardRow = document.getElementById(`li_${radioGroupName}_${OnlinePaymentOption.CREDIT_CARD}`);
            (creditCardRow ?? parentDiv).insertAdjacentElement("afterend", creditCardExpansionPanel);
        }

        // Reflect the current (possibly null) selection rather than always showing the panel --
        // _paymentOptionOnClick below (when it runs) will refine this further.
        this._syncCreditCardRowBorder();

        this._renderCashRoundingMessage(payOptions, parentDiv);

        if (this._item.paymentOptionId)
            this._paymentOptionOnClick(this._item.paymentOptionId, isFirstTime);
    }

    private _resetPaymentOptionState(): void {
        Util.setElementClass("remove", "div_checkout_hl_payment_options_credit", "accordion-open");
        Util.setElementClass("remove", "div_checkout_other_payment_options_credit", "accordion-open");
        Util.setElementClass("remove", "div_checkout_op_payment_options_credit", "accordion-open");
        this._item.paymentOptionId = null;
        this._item.customerWalletCard = null;
    }

    /**
     * Keeps the Credit/Debit row's own separator border in sync with whether its expansion
     * panel (div_checkout_credit_card_expansion) is currently shown, since the panel carries its
     * own bottom border when open -- otherwise the row would end up with two borders stacked.
     */
    private _syncCreditCardRowBorder(): void {
        const creditCardRow = document.getElementById(`li_checkout_payments_${OnlinePaymentOption.CREDIT_CARD}`);
        const isExpanded = this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD
            || this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD_WALLET;
        creditCardRow?.classList.toggle("has-expansion", isExpanded);
    }

    private _buildPaymentOptions(payOptions: IPaymentOptions, radioGroupName: string): IPaymentOptionElement[] {
        if (this._context.getProperty("qrCode") || this._context.getProperty("isOrderHub"))
            return this._buildQrCodePaymentOptions(payOptions, radioGroupName);
        return this._buildStandardPaymentOptions(payOptions, radioGroupName);
    }

    private _buildQrCodePaymentOptions(payOptions: IPaymentOptions, radioGroupName: string): IPaymentOptionElement[] {
        const creditCardOption = this._createCreditCardOption(payOptions, radioGroupName);
        return creditCardOption ? [creditCardOption] : [];
    }

    private _buildStandardPaymentOptions(payOptions: IPaymentOptions, radioGroupName: string): IPaymentOptionElement[] {
        this._areWalletsHiddenForFutureDate = this._shouldHideWalletsForFutureDate();
        const options = [
            this._createGooglePayOption(radioGroupName),
            this._createApplePayOption(radioGroupName),
            this._createCreditCardOption(payOptions, radioGroupName),
            this._createPayInStoreOption(payOptions, radioGroupName),
            this._createCashOption(payOptions, radioGroupName),
            this._createCreditOnDeliveryOption(payOptions, radioGroupName)
        ];
        return options.filter((opt): opt is IPaymentOptionElement => opt !== null);
    }

    private _resolveSelectedOption(options: IPaymentOptionElement[]): void {
        if (options.some(o => o.isSelected) || options.length === 0) return;
        const prev = options.find(o => o.id === this._item.paymentOptionId);
        if (prev) prev.isSelected = true;
    }

    private _renderCashRoundingMessage(payOptions: IPaymentOptions, parentDiv: HTMLElement): void {
        const roundingRule = this._data.getProperty("CashRoundingRule").roundingRule;
        if (roundingRule === RoundingRule.DO_NOT_ROUND || (!payOptions.PayInStore && !payOptions.Cash))
            return;

        const country = this._data.getProperty("StoreInfo").country;
        const roundingValue = Util.formatMoney(this._data.getProperty("CashRoundingRule").roundTo, country);

        const html = `
                    <div id="div_cash_rounding_message" class="cash-rounding-message">
                    <span ltag="CashRounding">*${this._localizationService.names("CASH_ROUNDING_MESSAGE").replace("{{roundingValue}}", roundingValue)}</span>
                    </div>`;

        const element = Util.createHtmlElementFromTemplate(html);
        parentDiv.appendChild(element);
    }

    private _createPayInStoreOption(payOptions: IPaymentOptions, radioGroupName: string): IPaymentOptionElement | null {
        // Pay In Store Option (rdo_checkout_in_store)
        const amountDue = this._order.getProperty("AmountDue");
        if (((payOptions.PayInStore && payOptions.PayInStoreAlternate == 0) ||
            (payOptions.PayInStore && payOptions.PayInStoreAlternate > 0 && amountDue < payOptions.PayInStoreLimit))
            && !this._item.curbsideInfoIsCurbside
            && (!this._modules.locationSection.contactless || this._order.getProperty("OrderTypeID") !== OrderType.DELIVERY)) {
            return {
                id: OnlinePaymentOption.PAY_IN_STORE,
                name: this._localizationService.names("PayInStore"),
                iconHtml: `<span class="pill-store-badge"></span>`,
                radioGroupName,
                isSelected: this._item.paymentOptionId === OnlinePaymentOption.PAY_IN_STORE,
                ltag: "PayInStore",
                onSelect: () => {
                    const btn = document.getElementById(`li_${radioGroupName}_${OnlinePaymentOption.PAY_IN_STORE}`) ?? undefined;
                    const humanClicked = btn?.dataset.humanClick === 'true';
                    this._paymentOptionOnClick(OnlinePaymentOption.PAY_IN_STORE, humanClicked);
                }
            };
        }

        return null;
    }

    private _createCashOption(payOptions: IPaymentOptions, radioGroupName: string): IPaymentOptionElement | null {
        // Cash Option (rdo_checkout_cash)
        const amountDue = this._order.getProperty("AmountDue");
        if (((payOptions.Cash && payOptions.CashAlternate == 0) ||
            (payOptions.Cash && payOptions.CashAlternate > 0 && amountDue < payOptions.CashLimit))
            && !this._item.curbsideInfoIsCurbside
            && (!this._modules.locationSection.contactless || this._order.getProperty("OrderTypeID") !== OrderType.DELIVERY)) {
            return {
                id: OnlinePaymentOption.CASH,
                name: this._localizationService.names("Cash"),
                iconHtml: `<span class="pill-cash-badge"></span>`,
                radioGroupName,
                isSelected: this._item.paymentOptionId === OnlinePaymentOption.CASH,
                ltag: "Cash",
                onSelect: () => {
                    const btn = document.getElementById(`li_${radioGroupName}_${OnlinePaymentOption.CASH}`) ?? undefined;
                    const humanClicked = btn?.dataset.humanClick === 'true';
                    this._paymentOptionOnClick(OnlinePaymentOption.CASH, humanClicked);
                }
            };
        }

        return null;
    }

    private _createCreditCardOption(payOptions: IPaymentOptions, radioGroupName: string): IPaymentOptionElement | null {
        let generateButton = false;

        if (payOptions.CreditCard &&
            (payOptions.CreditCardOption === CreditCardOption.CHARGE ||
                payOptions.CreditCardOption === CreditCardOption.PRE_VALIDATED)) {
            Util.showElement("div_checkout_credit");
            generateButton = true;

            if (payOptions.CardOnFile && this._user.isLoggedIn())
                Util.showElement("div_checkout_payment_existing");
            else
                Util.hideElement("div_checkout_payment_existing");

            // 2DO: Pre Auth message 
            //if (payOptions.CreditCardOption == CreditCardOption.PRE_VALIDATED) { 
            //    //Note: This credit card has pre-authorization for this purchase; the final payment will be charged in-store at pickup time. 
            //    Util.showElement("div_checkout_credit_card_pre_auth_note"); 
            //} 

            // 2DO: Hide credit card inputs as necessary  
            //if (payOptions.CreditCardAvsOption == CreditCardAvsOption.DO_NOT_VALIDATE) { 
            //    Util.hideElement("lbl_checkout_credit_address_chk"); 
            //    Util.hideElement("div_checkout_billing_add"); 
            //    Util.hideElement("lbl_checkout_credit_entry_billing_address"); 
            //    Util.hideElement("txt_checkout_credit_entry_billing_address"); 
            //    Util.hideElement("lbl_checkout_credit_entry_billing_zip"); 
            //    Util.hideElement("txt_checkout_credit_entry_billing_zip"); 
            //} else if (payOptions.CreditCardAvsOption == CreditCardAvsOption.ZIP_ONLY_PROMPT_VALIDATE) { 
            //    Util.hideElement("lbl_checkout_credit_entry_billing_address"); 
            //    Util.hideElement("txt_checkout_credit_entry_billing_address"); 
            //} else if (payOptions.CreditCardAvsOption == CreditCardAvsOption.ADDRESS_ONLY_PROMPT_VALIDATE) { 
            //    Util.hideElement("lbl_checkout_credit_entry_billing_zip"); 
            //    Util.hideElement("txt_checkout_credit_entry_billing_zip"); 
            //} 
        }

        const walletDiv = document.getElementById("div_checkout_payment_existing_cards");
        if (walletDiv) {
            walletDiv.innerHTML = "";

            const wallet = this._getWalletForGivenStore();

            if (this._showUserWallet()) {
                wallet.forEach((creditCard, index) => {
                    if (creditCard.token !== "") {
                        const card = this._createWalletItemHtml(index, creditCard, radioGroupName);
                        walletDiv.appendChild(card);
                    }
                });
                generateButton = true;
            }
        }

        if (generateButton) {
            return {
                id: OnlinePaymentOption.CREDIT_CARD,
                name: this._localizationService.names("CreditDebit"),
                iconHtml: `<span class="pill-credit-card-badge"></span>`,
                radioGroupName,
                // selected when model says we're on credit (wallet or new)
                isSelected: this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD ||
                    this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD_WALLET,
                ltag: "CreditDebit",
                onSelect: () => {
                    const btn = document.getElementById(`li_${radioGroupName}_${OnlinePaymentOption.CREDIT_CARD}`) ?? undefined;
                    const humanClicked = btn?.dataset.humanClick === 'true';
                    this._paymentOptionOnClick(OnlinePaymentOption.CREDIT_CARD, humanClicked);
                }
            };
        }

        return null;
    }

    /**
     * Returns the wallet cards applicable to the current store.
     *
     * The result always includes cards that belong directly to the current store.
     * If the current ecommerce merchant supports omni tokens, it also includes
     * omni-token cards from the user's wallet that belong to the same processor.
     *
     * Cards are de-duplicated by token so the same card is not returned more than once.
     *
     * @returns The list of wallet cards available for the current store context.
     */
    private _getWalletForGivenStore(): IUserCard[] {
        const userCards = this._user.getProperty("Wallet") ?? [];
        const storeId = this._data.getProperty("StoreInfo").storeId;
        const merchant = this._getEcommerceMerchant();

        const storeCards = userCards.filter(c => c.storeId === storeId);

        if (!merchant?.isOmniToken)
            return storeCards;

        const seenTokens = new Set<string>();
        const result: IUserCard[] = [];

        for (const card of storeCards) {
            if (seenTokens.has(card.token))
                continue;

            seenTokens.add(card.token);
            result.push(card);
        }

        for (const card of userCards) {
            if (card.isOmniToken &&
                card.processorId === merchant.ProcessorId &&
                !seenTokens.has(card.token))
            {
                seenTokens.add(card.token);
                result.push(card);
            }
        }

        return result;
    }

    /**
     * Returns the first merchant configured for ecommerce transactions.
     *
     * A merchant is considered an ecommerce merchant when its transaction type
     * contains "E". If no such merchant exists, null is returned.
     *
     * @returns The first ecommerce merchant, or null when none is available.
     */
    private _getEcommerceMerchant(): IDataMerchant | null {
        return this._data.getProperty("Merchants")?.find(x => x.TransactionType.includes("E")) ?? null;
    }

    private _createWalletItemHtml(id: number, creditCard: IUserCard, buttonId: string): HTMLElement {
        const isChecked = (this._item.customerWalletCard === id);
        const cvv = this._createWalletItemCvvHtml(id, isChecked);

        const html = `
              <label id="lbl_checkout_payment_wallet_option_${id}" class="checkout-payment-wallet-option" for="chk_checkout_payment_wallet_option_${id}">
    
                <div class="customize-item-modifier-label checkout-payment-wallet-label">

                  <!-- Top Row: Card Type + Radio -->
                  <div class="card-header-row">
                    <span class="card-label">${Util.getCreditCardTypeById(creditCard.cardTypeId)}...${creditCard.last4}</span>

                    <div class="custom-radio">
                      <input type="radio" id="chk_checkout_payment_wallet_option_${id}" name="chk_checkout_payment_wallet_option" data-id="${id}" ${isChecked ? "checked" : ""}>
                      <span class="checkmark"></span>
                    </div>
                  </div>

                  <!-- Bottom Row: Expiration + CVV -->
                  <div class="card-details-row">
                    ${this._isHeartland() ? '' : `<span><span ltag="Expires"></span> ${creditCard.expirationMonthNumber}/${creditCard.expirationYearNumber}</span><br>` }
                    ${cvv}
                  </div>

                </div>

              </label>`;

        const div = Util.createHtmlElementFromTemplate(html);

        // The radio is now a real, focusable overlay over the checkmark (see checkout.css), so it
        // reliably fires "change" for every way a user can select it: mouse click anywhere in the
        // label (native label-to-control forwarding), Space, or arrow-key radio-group navigation.
        const checkbox = div.querySelector<HTMLInputElement>(`#chk_checkout_payment_wallet_option_${id}`);
        checkbox?.addEventListener("change", () => this._walletCardOnClick(id, buttonId));

        const cvvInput = div.querySelector<HTMLInputElement>(`#txt_checkout_payment_wallet_cvv_${id}`);
        if (cvvInput) cvvInput.onclick = (e: Event) => { e.stopPropagation(); };

        return div;
    }

    private _createWalletItemCvvHtml(id: number, isChecked: boolean): string {
        const isCvv = this._showCreditCardWalletPaymentCvv();
        if (!isCvv)
            return "";

        const html = `
            <div id="div_checkout_payment_wallet_cvv_${id}" class="form-group ${isChecked ? "" : "hidden"}">
                <label for="txt_checkout_payment_wallet_cvv_${id}"><span class="required-star" ltag="SecurityCodeCVV">Security Code (CVV)</span></label>
                <input type="text" id="txt_checkout_payment_wallet_cvv_${id}" min="100" max="9999" placeholder="123" aria-describedby="spn_checkout_payment_wallet_cvv_error_${id}">
                <p id="spn_checkout_payment_wallet_cvv_error_${id}" class="error-text hidden" ltag="InvalidCVV">Invalid CVV.</p>
            </div>`;
        return html;
    }

    /**
     * Selects a wallet card, hides all wallet CVV fields, shows the selected card's CVV,
     * and activates wallet card payment option behavior.
     *
     * @param id Wallet card id.
     * @param buttonId Base button id used to find the wallet option element.
     */
    private _walletCardOnClick(id: number, buttonId: string): void {
        this._item.customerWalletCard = id;
        const btn = document.getElementById(`li_${buttonId}_${OnlinePaymentOption.CREDIT_CARD_WALLET}`) ?? undefined;

        // Show and hide CVVs
        this._hideWalletCvvs();
        Util.showElement(`div_checkout_payment_wallet_cvv_${id}`);

        const humanClicked = btn?.dataset.humanClick === 'true';
        this._paymentOptionOnClick(OnlinePaymentOption.CREDIT_CARD_WALLET, humanClicked);
    }

    /**
     * Hides all rendered wallet CVV fields.
     */
    private _hideWalletCvvs(): void {
        const cvvs = Array.from(document.querySelectorAll<HTMLElement>('[id^="div_checkout_payment_wallet_cvv_"]'));
        cvvs.forEach(x => Util.hideElement(x.id));
    }

    private readonly _addNewCardOnClick = (): void => {
        if (this._isNewCardFormOpen()) {
            this._closeNewCardForm();
        } else {
            this._openNewCardForm();
        }
    }

    private _isNewCardFormOpen(): boolean {
        return document.getElementById("spn_checkout_add_new_card_icon")?.classList.contains("open") ?? false;
    }

    private _openNewCardForm(): void {
        this._hideWalletCvvs();

        Util.setElementClass("add", "spn_checkout_add_new_card_icon", "open");
        const merchant = this._data.getProperty("Merchants").find(x => x.TransactionType.toUpperCase().includes('E'));
        if (merchant?.ProcessorId === 4) { //hearthland
            Util.setElementClass("add", "div_checkout_hl_payment_options_credit", "accordion-open");
            Util.setElementClass("remove", "div_checkout_other_payment_options_credit", "accordion-open");
            this.resetHeartland();
            Util.setElementClass("remove", "div_checkout_op_payment_options_credit", "accordion-open");
        } else if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants"))) {
            Util.setElementClass("remove", "div_checkout_hl_payment_options_credit", "accordion-open");
            Util.setElementClass("remove", "div_checkout_other_payment_options_credit", "accordion-open");
            Util.setElementClass("add", "div_checkout_op_payment_options_credit", "accordion-open");
            this.populateExpirationYearsDropdown(10);
        } else {
            Util.setElementClass("remove", "div_checkout_op_payment_options_credit", "accordion-open");
            Util.setElementClass("remove", "div_checkout_hl_payment_options_credit", "accordion-open");
            Util.setElementClass("add", "div_checkout_other_payment_options_credit", "accordion-open");
        }

        if (this._user.isLoggedIn()) {
            Util.showElement("div_checkout_payment_token");
        } else {
            Util.hideElement("div_checkout_payment_token");
        }

        this._item.paymentOptionId = OnlinePaymentOption.CREDIT_CARD;
        this._item.customerWalletCard = null;

        const wallets = Array.from(document.getElementsByName("chk_checkout_payment_wallet_option")) as HTMLInputElement[];
        for (const card of wallets) {
            card.checked = false;
        }
    }

    private _closeNewCardForm(walletClick: boolean = false): void {
        Util.setElementClass("remove", "spn_checkout_add_new_card_icon", "open");
        Util.setElementClass("remove", "div_checkout_hl_payment_options_credit", "accordion-open");
        Util.setElementClass("remove", "div_checkout_other_payment_options_credit", "accordion-open");
        Util.setElementClass("remove", "div_checkout_op_payment_options_credit", "accordion-open");
        Util.hideElement("div_checkout_payment_token");
        this._item.creditCardInfoCardNumber = "";
        this._item.creditCardInfoExpirationDate = "";
        this._item.creditCardInfoAddress = "";
        this._item.creditCardInfoZip = "";
        this._item.creditCardInfoCvv = "";
        this._item.creditCardInfoSaveToken = true;

        if (this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD
            || (this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD_WALLET && !walletClick)) {
            const cardDiv = document.querySelector('[id^="lbl_checkout_payment_wallet_option_"]') as HTMLElement | undefined;
            cardDiv?.click();
        }
    }

    private _createCreditOnDeliveryOption(payOptions: IPaymentOptions, radioGroupName: string): IPaymentOptionElement | null {
        // Credit/Debit On Delivery Option (rdo_checkout_credit_on_delivery)
        if (payOptions.CreditCard && payOptions.CreditCardOption == CreditCardOption.CHARGE_UPON_DELIVERY) {
            return {
                id: OnlinePaymentOption.CREDIT_CARD_UPON_DELIVERY,
                name: this._localizationService.names("CreditCardUponDelivery"),
                iconHtml: `<span class="pill-credit-card-badge"></span>`,
                radioGroupName,
                isSelected: this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD_UPON_DELIVERY,
                ltag: "CreditCardUponDelivery",
                onSelect: () => {
                    const btn = document.getElementById(`li_${radioGroupName}_${OnlinePaymentOption.CREDIT_CARD_UPON_DELIVERY}`) ?? undefined;
                    const humanClicked = btn?.dataset.humanClick === 'true';
                    this._paymentOptionOnClick(OnlinePaymentOption.CREDIT_CARD_UPON_DELIVERY, humanClicked);
                }
            };
        }

        return null;
    }

    private readonly _paymentOptionOnClick = (paymentOptionId: OnlinePaymentOption, sendDataLayerEvent: boolean = false): void => {
        const paymentOptionChanged = this._item.paymentOptionId != paymentOptionId;
        this._item.paymentOptionId = paymentOptionId;

        Util.showElement("btn_checkout_place_order");
        Util.showElement("btn_checkout_place_order_mobile");
        Util.hideElement("btn_checkout_apple_pay_bottom");
        Util.hideElement("btn_checkout_apple_pay_bottom_mobile");
        Util.hideElement("div_checkout_google_pay_bottom");
        Util.hideElement("div_checkout_google_pay_bottom_mobile");
        Util.hideElement("div_checkout_credit_card_expansion");

        switch (paymentOptionId) {
            case OnlinePaymentOption.PAY_IN_STORE:
                this._payInStorePaymentOption();
                break;
            case OnlinePaymentOption.CASH:
                this._cashPaymentOption();
                break;
            case OnlinePaymentOption.CREDIT_CARD:
                this._creditCardPaymentOption(paymentOptionChanged);
                break;
            case OnlinePaymentOption.CREDIT_CARD_UPON_DELIVERY:
                this._creditCardUponDeliveryPaymentOption();
                break;
            case OnlinePaymentOption.CREDIT_CARD_WALLET:
                this._creditCardWalletPaymentOption();
                break;
            case OnlinePaymentOption.APPLE_PAY:
                this._applePayPaymentOption();
                break;
            case OnlinePaymentOption.GOOGLE_PAY:
                this._googlePayPaymentOption();
                break;
            default:
                break;
        }

        this._syncCreditCardRowBorder();
        this._syncDatePickerLock();

        if (this._dataLayer && sendDataLayerEvent)
            this._dataLayer.add_payment_info(this._order, this._item.curbsideInfoIsCurbside);

        if (paymentOptionChanged)
            this._renderTipsSection();
    }

    private _payInStorePaymentOption(): void {
        Util.hideElement("div_checkout_payment_existing");
        this._closeNewCardForm();
        this._order.setProperty("PaymentType", PaymentType.PAY_IN_STORE);
    }

    private _cashPaymentOption(): void {
        Util.hideElement("div_checkout_payment_existing");
        this._closeNewCardForm();

        const payOptions = this._getPaymentOptionSettings();
        if (payOptions && this._order.getProperty("SubTotal") > payOptions.CashLimit) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService
                    .names("Error_OrderExceededCashLimit")
                    .replace(
                        "{{money}}",
                        Util.formatMoney(
                            payOptions.CashLimit,
                            this._data.getProperty("StoreInfo").country
                        )
                    ),
                true
            );

            if (this._item.paymentOptions)
                this._item.paymentOptions.clear();

            this._order.setProperty("PaymentType", undefined);
        } else {
            this._order.setProperty("PaymentType", PaymentType.CASH);
        }
    }

    private _creditCardPaymentOption(isNewSelection: boolean = false): void {
        Util.showElement("div_checkout_credit_card_expansion");
        const hasWallet = this._showUserWallet();

        if (hasWallet) {
            Util.showElement("div_checkout_payment_existing");

            // Default to the most recently saved card the first time the user picks Credit/Debit,
            // without clobbering an in-progress "add new card" entry on a later re-render.
            if (isNewSelection && this._item.customerWalletCard === null) {
                const wallet = this._getWalletForGivenStore();
                this._item.customerWalletCard = wallet.length - 1;
            }

            if (this._item.customerWalletCard !== null) {
                this._item.paymentOptionId = OnlinePaymentOption.CREDIT_CARD_WALLET;
                this._order.setProperty("PaymentType", PaymentType.WALLET_CREDIT_CARD);
                this._closeNewCardForm();
                return;
            }

            this._openNewCardForm();
            this._order.setProperty("PaymentType", PaymentType.CREDIT_CARD);
            return;
        }

        Util.hideElement("div_checkout_payment_existing");
        this._openNewCardForm();
        this._order.setProperty("PaymentType", PaymentType.CREDIT_CARD);
        if (this._data.getProperty("Settings").CCI && this._isHeartland()) {
            if (this._context.getProperty("heartland").isSet && this._context.getProperty("heartland").hps)
                this.resetHeartland();
            else
                this.setHeartland();
        }
    }

    private _creditCardUponDeliveryPaymentOption(): void {
        if (this._data.getProperty("StoreInfo").brandId === "A5985C38-D485-4240-8C01-931F7186D567" && !this._data.getProperty("Settings").CCI) {
            this._order.setProperty("PaymentType", PaymentType.CREDIT_CARD_MOBILE);
            // 2DO: Util.showElement("lbl_checkout_credit_expect_call"); 
            //<span id="lbl_checkout_credit_expect_call" ltag="CreditcardExpectCall" class="title3 center block p1-tb ">You will receive a call from the store to get your credit card information.</span> 
        }
    }

    private _creditCardWalletPaymentOption(): void {
        Util.showElement("div_checkout_credit_card_expansion");
        this._order.setProperty("PaymentType", PaymentType.WALLET_CREDIT_CARD);
        this._closeNewCardForm(true);
    }

    private _showCreditCardWalletPaymentCvv(): boolean {
        const payOptions = this._getPaymentOptionSettings();
        if (payOptions
            && (payOptions.CardOnFileCvvOption == CardOnFileCvvOption.MUST_MATCH
                || (payOptions.CardOnFileCvvOption == CardOnFileCvvOption.MUST_MATCH_OVER_THERSHOLD && payOptions.CardOnFileCvvThreshold < this._order.getProperty("AmountDue"))))
            return true;

        return false;
    }

    private readonly _showUserWallet = (): boolean => {
        const payOptions = this._getPaymentOptionSettings();
        const wallet = this._getWalletForGivenStore();
        if (payOptions?.CardOnFile && this._user.isLoggedIn() && wallet && wallet.length > 0)
            return true;

        return false;
    }

    private readonly _createOrderSummary = (): void => {
        const component = new OrderPriceSummary(this._eventBus, "div_checkout_totals", this._data, this._context, this._localizationService, this._order, this._loyaltyProvider, this._profileService);
        component.init();
    }

    private readonly _createFinalSection = (): void => {
        const message = this._data.getProperty("Settings").OOS.checkoutPageMessage;
        if (message) {
            Util.setElement("innerHTML", "p_checkout_store_message", message);
            Util.showElement("div_checkout_store_message");
        } else {
            Util.hideElement("div_checkout_store_message");
        }

        if (this._user.isLoggedIn())
            Util.hideElement("div_checkout_privacy_terms");
        else {
            Util.showElement("div_checkout_privacy_terms");

            const termsofUseUrl = new PrivacyTerms().getTermsOfUseUrl(this._data, this._localizationService);
            const privacyUrl = new PrivacyTerms().getPrivacyPolicyUrl(this._data, this._localizationService);

            const languages = this._localizationService.getSupportedLanguages();
            const nameObj: INewName[] = languages.map(lang => {
                const termsofUseUrl = new PrivacyTerms().getTermsOfUseUrl(this._data, this._localizationService, lang);
                const privacyUrl = new PrivacyTerms().getPrivacyPolicyUrl(this._data, this._localizationService, lang);
                const tempText = this._localizationService.names("ReadAndAcceptTermsOfUseAndPrivacyPolicy", lang).replace("{{terms_of_use}}", termsofUseUrl).replace("{{privacy_policy}}", privacyUrl);
                return { lc: lang, text: tempText };
            });

            Util.setElement("ltagj", "lbl_checkout_privacy_terms", Util.toLtagj(nameObj));
            Util.setElement("innerHTML", "lbl_checkout_privacy_terms", this._localizationService.names("ReadAndAcceptTermsOfUseAndPrivacyPolicy").replace("{{terms_of_use}}", termsofUseUrl).replace("{{privacy_policy}}", privacyUrl));
        }
    }

    /**
     * The Terms of Use / Privacy Policy links rendered above are plain target="_blank" anchors --
     * inside the wrapped app that would hand the native jsBridge-attached WebView to an arbitrary
     * external host, so in-app clicks are routed through the native openExternalLink bridge
     * instead, matching Login's _openTermsOfUse/_openPrivacyPolicy. Delegated on the stable
     * container since lbl_checkout_privacy_terms's innerHTML (and its embedded anchors) gets
     * replaced on every render.
     */
    private readonly _onPrivacyTermsLinkClick = (event: MouseEvent): void => {
        if (!Util.isAppView() || typeof openExternalLink !== "function")
            return;

        const anchor = (event.target as HTMLElement)?.closest('a[ltag="TermsOfUse"], a[ltag="PrivacyPolicy"]') as HTMLAnchorElement | null;
        if (!anchor)
            return;

        event.preventDefault();
        openExternalLink(anchor.href);
    }

    private _headerTotalUpdate(): void {
        if (document.getElementById("page_checkout")?.style.display === "none")
            return;

        const total = this._order.calculateOrderTotal();
        const name = `<span ltag="Total" style="text-transform: uppercase;">${this._localizationService.names("Total")}</span>: ${Util.formatMoney(total, this._data.getProperty("StoreInfo").country)}`;
        Router.path == Pages.CHECKOUT && this._eventBus.emit(EventTypes.HEADER_NAME_CHANGE, { name: name });
    }

    private readonly _renderNotificationsSection = (): void => {
        if (this._context.getProperty("isOrderHub")) {
            document.getElementById("div_checkout_notification")?.remove();
            return;
        }

        const notification = this._getNotificationSettings();

        if (!notification.IsNotify || this._context.getProperty("qrCode")) {
            Util.hideElement("div_checkout_notification");
        } else {
            Util.showElement("div_checkout_notification");

            if (this._user.isLoggedIn()) {
                if (notification.IsEmailNotify && !this._item.notificationInfoEmail)
                    this._item.notificationInfoEmail = this._user.getProperty("Email") || "";

                if (notification.IsTextNotify) {
                    this._item.notificationInfoCountryCodeId = this._user.getProperty("CountryCodeId") || 1;

                    if (!this._item.notificationInfoPhone)
                        this._item.notificationInfoPhone = this._user.getProperty("Phone") || "";
                }
            } else {
                this._item.notificationInfoEmail = "";
                this._item.notificationInfoCountryCodeId = 1;
                this._item.notificationInfoPhone = "";
            }

            if (notification.IsTextNotify) {
                Util.showElement("div_checkout_notification_text");
                this._item.notificationInfoIsNotifyPhone = true;
                this._notificationPhoneOnClick();
            } else
                Util.hideElement("div_checkout_notification_text");

            if (notification.IsEmailNotify) {
                Util.showElement("div_checkout_notification_email");
                this._item.notificationInfoIsNotifyEmail = true;
                this._notificationEmailOnClick();
            } else
                Util.hideElement("div_checkout_notification_email");
        }
    }

    private readonly _notificationPhoneOnClick = (): void => {
        const countryCodeInput = document.getElementById("dl_checkout_notification_country_code") as HTMLInputElement;
        const phoneInput = document.getElementById("txt_checkout_notification_phone") as HTMLInputElement;
        if (!countryCodeInput || !phoneInput)
            return;

        const isOn = this._item.notificationInfoIsNotifyPhone;
        countryCodeInput.disabled = !isOn;
        phoneInput.disabled = !isOn;
        isOn ? Util.showElement("div_checkout_notification_text_inputs") : Util.hideElement("div_checkout_notification_text_inputs");
    }

    private readonly _notificationEmailOnClick = (): void => {
        const input = document.getElementById("txt_checkout_notification_email") as HTMLInputElement;
        if (!input)
            return;

        const isOn = this._item.notificationInfoIsNotifyEmail;
        input.disabled = !isOn;
        isOn ? Util.showElement("div_checkout_notification_email_inputs") : Util.hideElement("div_checkout_notification_email_inputs");
    }

    /** False (after showing a message) when a delivery order's address is Do-NOT-Deliver, so Place Order must stop. */
    private readonly _checkDeliveryAddressExceptionAsync = async (): Promise<boolean> => {
        const address = this._order.getProperty("Address");
        if (this._order.getProperty("OrderTypeID") !== OrderType.DELIVERY || !address)
            return true;

        const blocked = await isAddressBlockedAsync(this._onlineOrderingService, address, this._data.storeKey);
        if (blocked)
            showBlockedAddressError(this._localizationService, this._dataLayer, address);

        return !blocked;
    }

    private readonly _checkMinimumDeliveryOrderAsync = async (): Promise<boolean> => {
        if (this._order.getProperty("OrderTypeID") !== OrderType.DELIVERY)
            return true;

        const address = this._order.getProperty("Address");
        const zoneMinDelAmount = Util.Float2(this._getMinimumDeliveryAmountForZone(address?.ZoneID));
        if (this._order.getProperty("SubTotal") - this._order.getProperty("Discount") < Util.Float2(this._order.getProperty("OrderTypeSubType").MinAmount) + zoneMinDelAmount) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService
                    .names("MinDelAmt")
                    .replace(
                        "??",
                        Util.formatNumber(
                            this._order.getProperty("OrderTypeSubType")
                                .MinAmount + zoneMinDelAmount
                        )
                    ),
                true
            );
            return false;
        }

        if ((this._data.getProperty("Settings").CRGMIL > 0 || this._data.getProperty("Settings").EMILES > 0) && (!!address?.Latitude && !!address?.Longitude)) { //setting 107 & 86
            const zone = this._data.getProperty("DeliveryZones").find(zone => zone.ZoneId === address?.ZoneID);
            await this._order.setOrderDeliveryDistanceFromStoreAsync(zone?.ZoneCharge ?? 0);
        }

        return true;
    }

    private readonly _getMinimumDeliveryAmountForZone = (zoneId: number | undefined): number => {
        const deliveryZones = this._data.getProperty("DeliveryZones");
        if (!zoneId || deliveryZones.length === 0)
            return 0;

        const foundZone = deliveryZones.find(zone => zone.ZoneId === zoneId);
        return foundZone ? foundZone.ZoneMinDeliveryAmount : 0;
    }

    private _wireHeartlandHandler(hps: any) {
        if (this._hpsWired) return;
        this._hpsWired = true;

        hps.Messages.onMessage = (msg: any) => {
            if (msg?.type !== 'HPS' || msg?.action !== 'tokenize') return;

            if (msg?.response?.token) {
                const token = msg.response.token.value ?? msg.response.token;
                this._context.setProperty("heartland", {
                    ...(this._context.getProperty("heartland") || {}),
                    token
                });

                // Re-enter submit flow, telling it this is the post-token call
                // This avoids the early-return guard (isSubmitting && !heartlandSubmit)
                this._submitAsync(true).catch(console.error);
            } else {
                // Tokenization failed — clear submitting state so the user can retry
                this._item.isSubmitting = false;
                console.error('Heartland tokenize error:', msg?.error ?? msg);
                // surface an error to the UI if you have a notifier
            }
        };
    }

    private readonly _submitAsync = async (heartlandSubmit: boolean): Promise<void> => {
        if (this._item.isSubmitting && !heartlandSubmit) return;

        this._item.isSubmitting = true;

        try {
            const isCreditCardPayment =
                this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD ||
                this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD_WALLET;

            const heartland = this._context.getProperty("heartland") || {};
            const hps = heartland.hps;

            if (this._isHeartland() && isCreditCardPayment && hps && !heartland.token) {
                this._wireHeartlandHandler(hps);
                const pkey = this.getHeartlandPublicKey();
                hps.Messages.post({ accumulateData: true, action: 'tokenize', message: pkey }, 'cardNumber');
                // Early return is fine now — the handler will call _submitAsync(true)
                return;
            }

            // We have a token now (either already had it or callback re-entered)
            if (await this._checkSubmitDataAsync() && await this._modules.timeSection.confirmLimitedTimeReservationAsync()) {
                await this._saveOrderAsync(); // call sale inside here or in a helper
                this._item.isSubmitting = false;
            } else {
                this._item.isSubmitting = false;
            }
        } catch (ex) {
            this._item.isSubmitting = false;
            console.error(ex);
        }
    };

    private async _checkSubmitDataAsync(): Promise<boolean> {
        this._hideErrorElements();

        // Reset ADA announcer for this submit attempt
        Util.resetAnnounceCycle();

        if (!this._checkSubmitDataOrderTypeOffered()) return false;
        if (!this._checkSubmitDataDeliveryAddress()) return false;
        if (!this._checkSubmitDataGiftCard()) return false;

        const payOptions = this._getPaymentOptionSettings();
        const fields: IValidationField[] = [];

        this._checkSubmitDataContactInfo(fields);
        this._checkSubmitDataFutureOrder(fields);
        this._checkSubmitDataCustomTip(fields);
        this._checkSubmitDataNotifications(fields);
        this._checkSubmitDataCurbside(fields);
        this._checkSubmitDataWallet(payOptions, fields);
        this._checkSubmitDataPayment(payOptions, fields);
        this._checkSubmitDataTermsAndAgreement(fields);


        Util.beginBatchAnnouncement();
        let failed = !InputValidator.validateFields(fields, true, true, this._localizationService, this._dataLayer);
        Util.endBatchAnnouncement();

        // Keep your existing FAILED override
        if (!failed && fields.some(x => x.type === FieldType.FAILED))
            failed = true;

        if (failed) {
            Util.scrollToFirstInvalidElement("page_checkout");

            Util.announceErrors(Util.collectVisibleErrors("page_checkout"));
        }

        return !failed;
    }


    // A store switch (e.g. a new delivery address) keeps the old order type when the new store doesn't offer it.
    private _checkSubmitDataOrderTypeOffered(): boolean {
        const orderTypeId = this._order.getProperty("OrderTypeID");
        if (this._data.getProperty("OrderTypeSubTypes").some(x => x.OrderTypeId === orderTypeId))
            return true;

        const orderTypeName = OnlineOrderingUtil.GetOrderTypeName(orderTypeId, this._data.getBrandProperty("OrderTypeSubTypes") ?? [], this._localizationService);
        DialogCreators.messageBoxOk(
            this._localizationService,
            this._dataLayer,
            this._localizationService.names("Error_OrderTypeUnavailableAtStore").replace("{{order_type}}", orderTypeName),
            true
        );
        return false;
    }

    private _checkSubmitDataDeliveryAddress(): boolean {
        if (this._order.getProperty("OrderTypeID") === OrderType.DELIVERY && !this._order.getProperty("Address")) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("NoDeliveryAddress"),
                true
            );
            return false;
        }
        return true;
    }

    private _checkSubmitDataGiftCard(): boolean {
        // Verify that there either is a payment option selected, and if not, completely payed by gift card
        const paidByGiftCard = this._checkOrderPaidByGiftCard();
        if (this._item.paymentOptionId === null && !paidByGiftCard) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("NoPayment"),
                true
            );
            return false;
        }

        // Clear any payment option if order is payed by gift cards
        if (paidByGiftCard)
            this._item.paymentOptionId = null;

        return true;
    }

    private _checkSubmitDataContactInfo(fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        // Contact Info Validation
        if (!this._user.isLoggedIn() && !this._context.getProperty("qrCode")) {
            fields.push({ type: FieldType.NAME, inputId: "txt_checkout_first_name", errorId: "spn_checkout_first_name_error" });
            fields.push({ type: FieldType.NAME, inputId: "txt_checkout_last_name", errorId: "spn_checkout_last_name_error" });
            fields.push({ type: FieldType.PHONE, inputId: "txt_checkout_phone", errorId: "spn_checkout_phone_error", extraIds: ["dl_checkout_country_code"] });
            fields.push({ type: FieldType.EMAIL, inputId: "txt_checkout_email", errorId: "spn_checkout_email_error" });
        }
    }

    private _checkSubmitDataFutureOrder(fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        if (this._modules.timeSection.getTimeSelection() !== TimeSelection.LATER) 
            return;

        if (!this._order.getProperty("FutureDate")) {
            fields.push({ type: FieldType.FAILED, inputId: "ddl_checkout_time_later_time", errorId: "spn_checkout_time_later_time_error" });
            return;
        }

        if (!this._isFutureDateValid()) {
            fields.push({ type: FieldType.FAILED, inputId: "ddl_checkout_time_later_time", errorId: "spn_checkout_time_later_time_error_validation" });
        }
    }

    private _isFutureDateValid(): boolean {
        const selectedDate = this._order.getProperty("FutureDate");
        const minutes = this._order.getProperty("FutureMinute");
        const nowMinutes = Util.getNowStoreMinutes(this._data);

        if (selectedDate === null || minutes < 0)
            return false;

        const selectedBusDate = Util.DateAdd(selectedDate, this._data.getProperty("Settings").BSM, 'm');
        const orderTypeSubType = this._order.getProperty("OrderTypeSubType");

        const startEndTimes = Util.GetDate_StartTime_EndTime(this._data, selectedBusDate, nowMinutes, false, orderTypeSubType);

        if (!startEndTimes.Online)
            return false;

        return (minutes >= startEndTimes.StartOrderMinute && minutes <= startEndTimes.EndOrderMinute);
    }

    private _checkSubmitDataCustomTip(fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        // Tip Validation
        if (Number(Util.cleanNumber(this._item.customTip, this._data.getProperty("StoreInfo").country)))
            fields.push({ type: FieldType.TIP, inputId: "txt_checkout_tip_custom", errorId: "spn_checkout_tip_custom_error" });
    }

    private _checkSubmitDataNotifications(fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        // Notification Validation
        const notification = this._getNotificationSettings();
        if (notification.IsNotify) {
            if (notification.IsTextNotify && this._item.notificationInfoIsNotifyPhone)
                fields.push({ type: FieldType.PHONE, inputId: "txt_checkout_notification_phone", errorId: "spn_checkout_notification_phone_error", extraIds: ["dl_checkout_notification_country_code"] });

            if (notification.IsEmailNotify && this._item.notificationInfoIsNotifyEmail)
                fields.push({ type: FieldType.EMAIL, inputId: "txt_checkout_notification_email", errorId: "spn_checkout_notification_email_error" });
        }
    }

    private _checkSubmitDataCurbside(fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        // Curbside Validation
        if (this._data.getProperty("Settings").CURBSD == Curbside.CUSTOMER_CAR_INFO
            && this._order.getProperty("OrderTypeID") === OrderType.TAKE_OUT
            && this._item.methodId === CheckoutMethodType.CURBSIDE) {
            fields.push({ type: FieldType.CAR, inputId: "txt_checkout_method_curbside_car_info", errorId: "spn_checkout_method_curbside_car_info_error" });
        }
    }

    private _checkSubmitDataWallet(payOptions: IPaymentOptions | null, fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        // Wallet Validation
        if (this._item.paymentOptionId !== OnlinePaymentOption.CREDIT_CARD_WALLET)
            return;

        if (this._item.customerWalletCard !== null) {
            if (payOptions && payOptions.CardOnFileCvvOption !== CardOnFileCvvOption.IGNORE)
                fields.push({ type: FieldType.CREDIT_CARD_CVV_SIMPLE, inputId: `txt_checkout_payment_wallet_cvv_${this._item.customerWalletCard}`, errorId: `spn_checkout_payment_wallet_cvv_error_${this._item.customerWalletCard}` });
        } else {
            fields.push({ type: FieldType.FAILED, inputId: "", errorId: "" });
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names("Error_SavedCardNotProperlySelected"));
        }
    }

    private _checkSubmitDataPayment(payOptions: IPaymentOptions | null, fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        // Payment Validation
        if (this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD && this._data.getProperty("Settings").CCI) {
            if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants"))) {
                this._checkSubmitDataOPPayment(payOptions, fields);
            } else if (!this._isHeartland()) {
                // To test Credit Card Number
                fields.push({ type: FieldType.CREDIT_CARD_NUMBER, inputId: "txt_checkout_payment_card_number", errorId: "spn_checkout_payment_card_number_error" });

                // To test Credit Card Expiration Date
                const timeOffset = this._data.getProperty("TimeOffset");
                const expiration = Util.getElementValue("txt_checkout_payment_expiration_date").split("/");
                const expMonth = expiration[0] ? Number(expiration[0]) : 0;
                const expYear = expiration[1] ? Number(expiration[1]) : 0;
                fields.push({ type: FieldType.CREDIT_CARD_EXP, inputId: "txt_checkout_payment_expiration_date", errorId: "spn_checkout_payment_expiration_date_error", extraData: [expMonth, expYear, timeOffset] });

                // To test Credit Card CVV
                const ccNumber = Util.getElementValue("txt_checkout_payment_card_number");
                fields.push({ type: FieldType.CREDIT_CARD_CVV, inputId: "txt_checkout_payment_cvv", errorId: "spn_checkout_payment_cvv_error", extraData: [ccNumber] });
            }

            this._checkCreditCardAvsOption(payOptions, fields);
        }
    }

    private _checkSubmitDataOPPayment(payOptions: IPaymentOptions | null, fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        fields.push({ type: FieldType.CREDIT_CARD_NUMBER, inputId: "txt_checkout_op_payment_card_number", errorId: "spn_checkout_op_payment_card_number_error" });

        // To test Credit Card Expiration Date
        const timeOffset = this._data.getProperty("TimeOffset");
        const expMonth = Number(Util.getElementValue("ddl_checkout_op_credit_entry_expiration_month")) || 0;
        const expYear = Number(Util.getElementValue("ddl_checkout_op_credit_entry_expiration_year")) || 0;
        fields.push({ type: FieldType.CREDIT_CARD_EXP, inputId: "txt_checkout_op_payment_expiration_date", errorId: "spn_checkout_op_payment_expiration_date_error", extraData: [expMonth, expYear, timeOffset] });

        // To test Credit Card CVV
        const ccNumber = Util.getElementValue("txt_checkout_op_payment_card_number");
        fields.push({ type: FieldType.CREDIT_CARD_CVV, inputId: "txt_checkout_op_payment_cvv", errorId: "spn_checkout_op_payment_cvv_error", extraData: [ccNumber] });
    }
    private _checkCreditCardAvsOption(payOptions: IPaymentOptions | null, fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        let payment_billing_address = "txt_checkout_payment_address";
        let payment_billing_zip = "txt_checkout_payment_zip_code";
        if (this._isHeartland()) {
            payment_billing_address = "txt_checkout_hl_payment_address";
            payment_billing_zip = "txt_checkout_hl_payment_zip_code";
        } else if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants"))){
            payment_billing_address = "txt_checkout_op_payment_address";
            payment_billing_zip = "txt_checkout_op_payment_zip_code";
        }
        if (payOptions && payOptions.CreditCardAvsOption != CreditCardAvsOption.DO_NOT_VALIDATE) {
            // To test Credit Card Address
            if (payOptions.CreditCardAvsOption != CreditCardAvsOption.ZIP_ONLY_PROMPT_VALIDATE)
                fields.push({ type: FieldType.CREDIT_CARD_ADDRESS, inputId: payment_billing_address, errorId: "spn_checkout_payment_address_error" });

            // To test Credit Card Zip
            if (payOptions.CreditCardAvsOption != CreditCardAvsOption.ADDRESS_ONLY_PROMPT_VALIDATE)
                fields.push({ type: FieldType.CREDIT_CARD_ZIP, inputId: payment_billing_zip, errorId: "spn_checkout_payment_zip_code_error" });
        }
    }

    private _checkSubmitDataTermsAndAgreement(fields: { type: FieldType, inputId: string, errorId: string, extraData?: any[], extraIds?: string[] }[]): void {
        // Terms and Agreement Validation
        if (!this._user.isLoggedIn() && !this._item.termsAndConditions)
            fields.push({ type: FieldType.CHECKED, inputId: "chk_checkout_privacy_terms", errorId: "spn_checkout_privacy_terms_error" });
    }

    private readonly _getNotificationSettings = (): IGetNotificationSettings => {
        const notificationData = this._data.getProperty("Notification");

        const isNotify = notificationData.hasNotifications;
        const isTextNotify = (isNotify && notificationData.hasTextNotifications);
        const isEmailNotify = (isNotify && notificationData.hasEmailNotifications);

        return {
            IsNotify: isNotify,
            IsTextNotify: isTextNotify,
            IsEmailNotify: isEmailNotify,
        };
    }

    private _hideErrorElements(): void {
        Util.hideElementErrors("txt_checkout_first_name", "spn_checkout_first_name_error");
        Util.hideElementErrors("txt_checkout_last_name", "spn_checkout_last_name_error");
        Util.hideElementErrors("txt_checkout_phone", "spn_checkout_phone_error");
        Util.hideElementErrors("txt_checkout_email", "spn_checkout_email_error");
        Util.hideElementErrors("ddl_checkout_time_later_time", "spn_checkout_time_later_time_error");
        Util.hideElementErrors("ddl_checkout_time_later_time", "spn_checkout_time_later_time_error_validation");
        Util.hideElementErrors("txt_checkout_payment_card_number", "spn_checkout_payment_card_number_error");
        Util.hideElementErrors("txt_checkout_payment_expiration_date", "spn_checkout_payment_expiration_date_error");
        Util.hideElementErrors("txt_checkout_payment_cvv", "spn_checkout_payment_cvv_error");
        Util.hideElementErrors("txt_checkout_payment_address", "spn_checkout_payment_address_error");
        Util.hideElementErrors("txt_checkout_payment_zip_code", "spn_checkout_payment_zip_code_error");
        Util.hideElementErrors("txt_checkout_method_curbside_car_info", "spn_checkout_method_curbside_car_info_error");
        Util.hideElementErrors("chk_checkout_privacy_terms", "spn_checkout_privacy_terms_error");
        Util.hideElementErrors("txt_checkout_notification_phone", "spn_checkout_notification_phone_error");
        Util.hideElementErrors("txt_checkout_notification_email", "spn_checkout_notification_email_error");
    }

    private _checkOrderPaidByGiftCard(): boolean {
        if (this._context.getProperty("isOrderHub"))
            return false;

        const total = this._order.calculateOrderTotal();
        let giftCardTotal = 0;

        for (const giftCard of this._order.getProperty("GiftCards"))
            giftCardTotal += giftCard.appliedAmount;

        return total == giftCardTotal;
    }

    private async _saveOrderAsync(): Promise<void>{
        const loader = new LoaderSpinner();
        try {
           
            await this._checkCurrentPromiseTimeAsync();
            const nOrder = this._composeOrderObject();
            await this._sendOrderToServerAsync(nOrder);
        } catch (ex: unknown) {
            console.error("[SaveOrder]", ex);

            this._item.isSubmitting = false;
        }
        loader?.destroy();
    }

    private readonly _checkCurrentPromiseTimeAsync = async (): Promise<void> => {
        const times = this._data.getProperty("WaitTimes");
        if (times.SET !== 3)
            return;

        const currentMinute = Util.getNowStoreMinutes(this._data);
        const response = await this._onlineOrderingService.getLastWaitTimes(this._data.getProperty("StoreInfo").storeKey);
        if (!response.success)
            return;

        if (!times.WTIM)
            return;

        const newTime = JSON.parse(response.data);
        const savedWaitTime = times.WTIM.find(x => x.MIN == (currentMinute - currentMinute % 5));

        if (!newTime)
            return;

        if (savedWaitTime) {
            const averageTime = Math.ceil((savedWaitTime.MAKEMIN + newTime.MAKEMIN) / 2);
            const averageDeliveryTime = Math.ceil((savedWaitTime.DELMIN + newTime.DELMIN) / 2);
            const currMinute = times.WTIM.find(x => x.MIN == (currentMinute - currentMinute % 5));
            if (currMinute) {
                currMinute.MAKEMIN = averageTime;
                currMinute.DELMIN = averageDeliveryTime;
            }
        } else {
            const time = times.WTIM.find(x => x.MIN == (currentMinute - currentMinute % 5));
            if (time) {
                time.MAKEMIN = newTime.MAKEMIN;
                time.DELMIN = newTime.DELMIN;
            } else {
                const newTimeObj = {
                    MIN: currentMinute - currentMinute % 5,
                    MAKEMIN: newTime.MAKEMIN,
                    DELMIN: newTime.DELMIN
                };
                times.WTIM.push(newTimeObj);
            }
        }

        const orderType = this._data.getProperty("OrderTypeSubTypes").find(x => x.OrderTypeId == this._order.getProperty("OrderTypeSubType").OrderTypeId);
        const waitTime = OnlineOrderingUtil.getWaitTimeByOrderType(this._data, orderType);
        this._order.setProperty("WaitTime", waitTime);
    }

    private readonly _sendOrderToServerAsync = async (nOrder: InOrder): Promise<void> => {
        if (!this._checkPaymentsFinal(nOrder)) {
            DialogCreators.messageBoxOk(this._localizationService, this._dataLayer, this._localizationService.names("Error_NoPaymentAttachedToOrder"));
            this._item.isSubmitting = false;
            return;
        }

        this._modules.timeSection.setCheckOrderTimeIntervalPaused(true);

        try {
            const sOrder = JSON.stringify(nOrder);
            const response = await this._onlineOrderingService.saveOrder(sOrder);

            try {

                if (response.success) {
                    nOrder.OrderID = response.orderId || 0;
                    this._order.setProperty("OrderID", nOrder.OrderID);

                    const orderHubKey = this._context.getProperty("orderHubKey");
                    if (orderHubKey)
                        await this._onlineOrderingService.updateOrderhubOrder(
                            orderHubKey,
                            this._order.getProperty("OrderID"),
                            this._data.storeKey);

                    const usedRewardIds = this._getRewardRedemptionIds();
                    await this._loyaltyProvider.postOrder(usedRewardIds);
                }
            } catch { }

            await this._orderResultAsync(nOrder, response);
        }
        finally {
            this._item.isSubmitting = false;
        }
    }

    private _checkPaymentsFinal(nOrder: InOrder): boolean {
        if (nOrder.Payments.length > 0)
            return true;

        if (this._item.paymentOptionId === OnlinePaymentOption.PAY_IN_STORE && [OrderType.DINE_IN, OrderType.TAKE_OUT].includes(nOrder.OrderTypeID))
            return true;

        if (this._item.paymentOptionId === null && nOrder.Total === 0)
            return true;

        return false;
    }

    private readonly _orderResultAsync = async (nOrder: InOrder, response: IServiceSaveOrder): Promise<void> => {
        this._item.isSubmitting = false;
        if (!response.orderId) {
            if (response.message)
            {
                // The unavailable-items message is a per-item sentence, so render one line for each item.
                let msg = response.message === "ITEMS_NO_LONGER_AVAILABLE"
                    ? (response.messageParams ?? []).map(name => this._localizationService.names(response.message, undefined, name)).join("<br>")
                    : this._localizationService.names(response.message, undefined, ...(response.messageParams ?? []));
                if (msg === "undefined" || msg === "")
                    msg = response.message;

                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    msg,
                    true
                );
            }
            return;
        }

        if (response.success) {
            await this._orderResultHandleSuccess(nOrder, response);
        } else {
            this._orderResultHandleFailure(response);
        }
    }

    private readonly _orderResultHandleSuccess = async (nOrder: InOrder, response: IServiceSaveOrder): Promise<void> => {
        if (!response.success || !response.orderId) return;

        this._mergePaymentsTransData(nOrder, response);

        await this._handlePostOrderCleanup(nOrder);
        await this._handleUserDataUpdates();
        await this._handleGiftCardUpdates(response);

        this._trackOrderCompletion(nOrder, response);
        this._completeOrderFlow(nOrder);
    }

    /**
     * Merges the server's post-charge TransData (see IServiceSaveOrder.paymentsTransData) back into
     * nOrder.Payments before the confirmation screen renders, so it can show the real last 4/card
     * logo instead of whatever pre-charge TransData the client submitted.
     * @private
     */
    private _mergePaymentsTransData(nOrder: InOrder, response: IServiceSaveOrder): void {
        response.paymentsTransData?.forEach((transData, i) => {
            if (transData && nOrder.Payments[i])
                nOrder.Payments[i].TransData = transData;
        });
    }

    private async _handlePostOrderCleanup(nOrder: InOrder): Promise<void> {
        this._modules.timeSection.clearLimitedTimesTimer();
        this._user.setDefaultStore(nOrder.StoreID);
        await this._profileService.updateProfileCredits();
        await this._loyaltyProvider.batchComparison(false);
    }

    private async _handleUserDataUpdates(): Promise<void> {
        // If a new card is saved, refresh user cards
        if (this._user.isLoggedIn() &&
            this._item.creditCardInfoSaveToken &&
            this._item.paymentOptionId == OnlinePaymentOption.CREDIT_CARD) {
            await this._profileService.getUserWalletAsync();
        }

        // Clear used Rewards
        const usedRewardIds = this._getRewardRedemptionIds();
        this._user.removeUsedRewards(usedRewardIds);
    }

    private async _handleGiftCardUpdates(response: IServiceSaveOrder): Promise<void> {
        const giftCards = this._order.getProperty("GiftCards");
        try {
            if (response.success && giftCards.length > 0) {
                const uuids = giftCards.map(x => String(x.uuid)).filter(Boolean);
                if (uuids.length > 0) {
                    await this._loyaltyProvider.fetchGiftCardsBalance(uuids);
                }
            }
        } catch {
            // Silently handle gift card balance fetch errors
        }
    }

    private _trackOrderCompletion(nOrder: InOrder, response: IServiceSaveOrder): void {
        if (this._dataLayer) {
            const isContactless = (this._modules.locationSection.contactless || false);
            const notificationType = this._getNotificationTypeForTracking();
            this._dataLayer.purchase(
                this._order,
                response.orderId!,
                isContactless,
                this._item.curbsideInfoIsCurbside, 
                notificationType
            );
        }
    }

    private _getNotificationTypeForTracking(): string {
        const phoneNotification = this._item.notificationInfoPhone ? OrderNotificationType.PHONE : undefined;
        const emailNotification = this._item.notificationInfoEmail ? OrderNotificationType.EMAIL : undefined;

        if (phoneNotification && emailNotification) {
            return `${phoneNotification}, ${emailNotification}`;
        }

        return phoneNotification ?? emailNotification ?? OrderNotificationType.NONE;
    }

    private _completeOrderFlow(nOrder: InOrder): void {
        this._eventBus.emit(EventTypes.ORDER_COMPLETED, nOrder);
        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CONFIRMATION);
    }

    private readonly _orderResultHandleFailure = (response: IServiceSaveOrder): void => {
        this._context.getProperty("heartland").token = undefined;
        this._context.getProperty("heartland").last4 = undefined;
        // Wallet tokens are single-use; clear them so retrying re-opens the payment sheet
        // (picking a different card) instead of resubmitting the same declined token.
        this._item.googlePayToken = null;
        this._item.applePayToken = null;
        this._item.isSubmitting = false;
        DialogCreators.messageBoxOk(
            this._localizationService,
            this._dataLayer,
            response.message,
            true
        );
        if (response.orderId) {
            this._order.setProperty("OrderID", response.orderId);
        }

        this._modules.timeSection.setCheckOrderTimeIntervalPaused(false);
    }

    private readonly _isHeartland = (): boolean => {
        const creditCardIntegration = this._data.getProperty("Settings").ISCC;
        const storeId = this._order.getProperty("StoreID");
        const heartlandPublicKey = this._data.getProperty("Settings").HPK;

        let pkey: string | null = null;
        if (creditCardIntegration) {
            const storeID = storeId;
            const store = Util.getCallCenterStore(storeID, this._data.getProperty("Stores"));
            pkey = store?.Heartland || "";
        } else {
            pkey = heartlandPublicKey;
        }

        return pkey ? true : false;
    }

    private getHeartlandPublicKey(): string | undefined {
        if (this._data.getProperty("Settings").ISCC) {
            const storeID = this._data.getProperty("StoreInfo").storeId;
            const store = Util.getCallCenterStore(storeID, this._data.getProperty("Stores"));
            return store?.Heartland || "";
        }

        return this._data.getProperty("Settings")?.HPK;
    }

    private resetHeartland(): void {
        try { this._context.getProperty("heartland").hps?.dispose(); } catch (ex) { }
        this._context.setProperty("heartland", {
            isSet: false,
            hps: undefined,
            token: undefined,
            last4: undefined
        });
        this.setHeartland();
    }

    private async SetHeartlandToken(token: string, last4: string): Promise<void> {
        this._context.getProperty("heartland").token = token;
        this._context.getProperty("heartland").last4 = last4;
        await this._submitAsync(true);
    }

    private setHeartland(): void {
        if(!this._isHeartland())
            return; 
        let heartlandContext = this._context.getProperty("heartland");
        if (!heartlandContext) {
            heartlandContext = {
                isSet: false,
                hps: undefined,
                token: undefined,
                last4: undefined
            };
            this._context.setProperty("heartland", heartlandContext);
        }

        if (heartlandContext.isSet)
            return;

        heartlandContext.isSet = true;
        const pkey = this.getHeartlandPublicKey();
        //@ts-ignore
        heartlandContext.hps = new Heartland.HPS({
            publicKey: pkey,
            style: {
                'input': {
                    'touch-action': 'manipulation',
                    'color': '#333333',
                    'font-size': '1rem',
                    'border': '0',
                    'border-bottom': '1px solid #cfcfcf',
                    'background-color': '#fefefe',
                    'font-family': '1.0rem RobotoCondensed, Arial, sans-serif',
                    '-webkit-appearance': 'none',
                    'border-radius': '0',
                    'padding': '0.75rem',
                    'cursor': 'text',
                    'width': '90%'
                },
                '::-webkit-input-placeholder': { /* WebKit, Blink, Edge */
                    'color': 'var(--colorText)'
                },
                ':-moz-placeholder': { /* Mozilla Firefox 4 to 18 */
                    'color': 'var(--colorText)',
                    'opacity': '1'
                },
                '::-moz-placeholder': { /* Mozilla Firefox 19+ */
                    'color': 'var(--colorText)',
                    'opacity': '1'
                },
                ':-ms-input-placeholder': { /* Internet Explorer 10-11 */
                    'color': 'var(--colorText)'
                }
            },
            type: 'iframe',
            fields: {
                cardNumber: { target: 'iframesCardNumber', placeholder: '•••• •••• •••• ••••' }
                , cardExpiration: { target: 'iframesCardExpiration', placeholder: 'MM / YY' }
                , cardCvv: { target: 'iframesCardCvv', placeholder: 'CVV' }
            },
            onTokenSuccess: (resp: any) => {
                console.error('Heartland tokenize success:', 'onTokenSuccess is fired');
                this.SetHeartlandToken(resp.token_value, resp.last_four);
            },
            onTokenError: (resp: any) => {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    resp.error.message,
                    true
                );
                this._item.isSubmitting = false;
                // Util.LogError("SetHeartland", resp.error.message, globalThis.aOLO);
            },
            onResize: function () {

            },
            onEvent: (ev: any) => {
                if (ev.type === 'keyup' && ev.source === 'cardNumber' && ev.classes.indexOf('valid') !== -1) {
                    this._context.getProperty("heartland").hps.setFocus('cardExpiration');
                } else if (ev.type === 'keyup' && ev.source === 'cardExpiration' && ev.classes.indexOf('valid') !== -1 &&
                    ev.data.keyCode != 8 && ev.data.keyCode != 37 && ev.data.keyCode != 38 && ev.data.keyCode != 39 && ev.data.keyCode != 40 && ev.data.keyCode != 46) {
                    this._context.getProperty("heartland").hps.setFocus('cardCvv');
                } else if (ev.type === 'keyup' && ev.source === 'cardCvv' && ev.classes.indexOf('valid') !== -1 && ev.data.keyCode == 13) {
                    const ccAddress = document.getElementById("txt_checkout_credit_entry_billing_address");
                    if (ccAddress)
                        ccAddress.focus();
                }
            }
        });

        this._labelHeartlandIframes();
    }

    /**
     * The Heartland hosted-fields SDK injects unlabeled iframes into the target containers, and
     * the visible "Card Number"/"Expiration Date"/"Security Code" text is a <span>, not a <label>,
     * since it can't reference an input hidden inside a cross-origin iframe. Setting each iframe's
     * own title gives it a programmatic accessible name for screen reader users.
     */
    private _labelHeartlandIframes(): void {
        const labels: [string, string][] = [
            ["iframesCardNumber", this._localizationService.names("CardNo")],
            ["iframesCardExpiration", this._localizationService.names("ExpDate")],
            ["iframesCardCvv", this._localizationService.names("SecurityCode")]
        ];

        for (const [containerId, title] of labels) {
            const iframe = document.getElementById(containerId)?.querySelector("iframe");
            if (iframe) iframe.title = title;
        }
    }

    private readonly _getRewardRedemptionIds = (): number[] => {
        const redemptionIds: number[] = [];
        this._user.getProperty("LoyaltyData")?.Rewards.forEach(x => {
            if (x?.Used && this._order.getProperty("Coupons").map(y => y.RewardId).includes(x.RewardId))
                redemptionIds.push(x.RewardId);
        });
        return redemptionIds;
    }

    private readonly _composeOrderObject = (): InOrder => {
        const Items_Modifiers_Taxes_Comments = this._completeItemsModifiersTaxesComments();
        if (!Items_Modifiers_Taxes_Comments)
            throw new Error('Could not create nOrder. CompleteItemsModifiersTaxesComments()');

        const nOrder = this._completeOrder();
        if (!nOrder)
            throw new Error('Could not create nOrder. CompleteOrder()');

        nOrder.Challenges = this._completeChallenges();
        nOrder.Comments = this._completeComments();
        nOrder.Coupons = this._completeCoupons();
        nOrder.ServiceCharges = this._completeServiceCharges();
        nOrder.Customer = this._completeCustomer();
        nOrder.Address = this._completeAddress();
        nOrder.OrderItems = Items_Modifiers_Taxes_Comments.Items;
        nOrder.ItemsComments = Items_Modifiers_Taxes_Comments.Comments;
        nOrder.ItemsModifiers = Items_Modifiers_Taxes_Comments.Modifiers;
        nOrder.ItemsTaxes = Items_Modifiers_Taxes_Comments.Taxes;
        nOrder.ItemsMakeList = this._completeMakeList(nOrder.PrintTime);
        nOrder.ItemsCoupons = this._completeItemsCoupons();
        nOrder.Taxes = this._completeTaxes();
        nOrder.Payments = this._completePayments();
        nOrder.SaveToken = this._item.creditCardInfoSaveToken;

        if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants"))) {
            nOrder.SaveToken = false
        }

        nOrder.Notify = this._item.notificationInfoIsNotifyPhone || this._item.notificationInfoIsNotifyEmail;
        nOrder.NotifyCountryCodeId = this._item.notificationInfoIsNotifyPhone ? this._item.notificationInfoCountryCodeId : null;
        nOrder.NotifyPhone = this._item.notificationInfoIsNotifyPhone ? this._item.notificationInfoPhone : null;
        nOrder.NotifyEmail = this._item.notificationInfoIsNotifyEmail ? this._item.notificationInfoEmail : null;

        nOrder.RedemptionIDs = this._getRewardRedemptionIds().map(x => x.toString());
        if (this._data.getLoyaltyInfo().providerId != LoyaltyProvider.ADORA)
            nOrder.RedemptionIDs = [];

        const customer: InOrderCustomerProfile = {
            CID: null,
            storeID: Number(this._data.getProperty("StoreInfo").storeId),
            mrkFavStore: false,
            cult: this._context.getProperty("languageCode"),
            mrkBD: null,
            mrkEmail: null,
            mrkTxt: null,
            mrkTxtPhone: null,
            NAM: nOrder.Customer?.Name || "",
            LNAM: nOrder.Customer?.LastName || "",
            mrkSurv: null,
            mrkLoy: null,
            zip: null,
            profId: null,
            PHN: nOrder.Customer?.Phone || "",
            EMAIL: nOrder.Customer?.Email || "",
            CCDID: nOrder.Customer?.CountryCodeId || 1
        };

        if (this._user.isLoggedIn()) {
            customer.CID = nOrder.Customer?.CustomerID || 0;
            customer.storeID = this._user.getProperty("StoreId") || 0;
        }

        nOrder.CustomerProfile = JSON.stringify(customer); // Verify

        if (this._order.getProperty("Fundraiser"))
            nOrder.Fundraiser = this._order.getProperty("Fundraiser");

        return nOrder;
    }

    private readonly _completeOrder = (): InOrder | null => {
        try {
            const timeOffset = this._data.getProperty("TimeOffset");
            const businessStartMinute = this._data.getProperty("Settings").BSM;
            const futureDate = this._order.getProperty("FutureDate");

            const now = Util.NowStore(timeOffset);
            let statusId = 1;
            let printTime = "";
            let promiseTime = "";
            if (futureDate) {
                const iDate = new Date(futureDate);
                const bDate = new Date(Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute));
                statusId = (Util.DateDiff("d", bDate, iDate) < 0) ? 7 : 6; // it is next day: 7 = future day, 6 = time order

                promiseTime = Util.formatDateTime(Util.DateAdd(iDate, this._order.getProperty("FutureMinute"), "m"), "en-US");
                printTime = this._getFuturePrintTime(iDate, new Date(promiseTime), this._data.getProperty("Settings").FPT, this._order.getProperty("WaitTime"));
            } else {
                printTime = Util.formatDateTimeSeconds(now);
                promiseTime = Util.formatDateTimeSeconds(Util.DateAdd(now, this._order.getProperty("WaitTime"), "m")); // 2DO: WaitTime is 0
            }

            let royaltyAmount = 0.00;
            let taxableAmount = 0.00;
            let giftCardSalesAmount = 0.00;
            let giftCertSalesAmount = 0.00;
            for (const oItem of this._order.getProperty("Items")) {
                const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId);
                if (mItem?.IsLoyalty)
                    royaltyAmount += Util.Float2((oItem.Price * oItem.Quantity) - oItem.BeforeTaxDiscount - oItem.AfterTaxDiscount);

                if (mItem?.IsGiftCard)
                    giftCardSalesAmount += Util.Float2(oItem.Price * oItem.Quantity);

                if (mItem?.IsGiftCert)
                    giftCertSalesAmount += Util.Float2(oItem.Price * oItem.Quantity);
                taxableAmount += oItem.TaxAmount;
            }

            if (Util.Float2(this._order.getProperty("OrderTypeSubType").Rate) > 0) {
                taxableAmount += Util.Float2(this._order.getProperty("OrderTypeCharge"));
            }

            royaltyAmount += Util.Float2(this._order.getProperty("OrderTypeCharge"));

            let donationAmount = 0;
            const donations = this._completeDonations();
            donations.forEach(x => donationAmount += x.Amount);

            const nOrder: InOrder = {
                CultureCode: this._context.getProperty("languageCode"),
                SourceID: 2,
                StoreID: this._order.getProperty("StoreID"),

                Key: this._order.getProperty("Key"),
                GUID: this._order.getProperty("GUID"),
                OrderID: this._order.getProperty("OrderID"),
                OrderTypeID: this._order.getProperty("OrderTypeID"),
                OrderTypeSubID: this._order.getProperty("OrderTypeSubID"),
                StationID: this._data.getProperty("Settings").STID,
                CustomerID: this._user.getProperty("CustomerId") || 0,
                PID: this._user.getProperty("ProfileId") || 0,
                AddressID: (this._order.getProperty("OrderTypeID") === OrderType.DELIVERY) ? this._order.getProperty("AddressID") : null,
                TableID: 0,
                DriveThroughID: 0,
                TakenByID: 0,
                OrderNo: 0,
                NameTableNo: this._context.getProperty("qrCode")?.tblno || "",

                OrderDate: Util.formatDate(this._order.getProperty("OrderStartTime") || new Date(), "en-US"),
                LoyaltyGroupID: this._data.getProperty("Settings").LGID,
                VPhone: this._user.getProperty("Phone") || "",
                Loyalty: this._user.getProperty("IsLoyalty") || false,
                PromiseTime: promiseTime,
                PrintTime: printTime,
                IsProcessed: false,
                ProcessorStationID: null,
                ProcessMarkTime: null,
                BUSDate: Util.formatDate(Util.GetBUSDate(new Date(promiseTime), timeOffset, businessStartMinute), "en-US"),
                IsFuture: (this._order.getProperty("FutureDate") !== null),
                OrderStartTime: Util.formatDateTimeSeconds(this._order.getProperty("OrderStartTime") || new Date()),
                OrderEndTime: Util.formatDateTimeSeconds(now),
                SubTotal: Util.Float2(this._order.getProperty("SubTotal") - this._order.getProperty("TaxInSubTotal")),
                Total: Util.Float2(this._order.getProperty("Total")),
                ServiceCharge: Util.Float2(this._order.getProperty("ServiceCharges").Amount),
                GiftCardSalesAmount: Util.Float2(giftCardSalesAmount),
                GiftCertSalesAmount: Util.Float2(giftCertSalesAmount),
                RoyaltyAmount: Util.Float2(royaltyAmount),
                TaxableAmount: Util.Float2(taxableAmount),
                TaxActual: Util.Float5(this._order.getProperty("Tax")),
                TaxCollected: Util.Float2(this._order.getProperty("Tax")),
                OrderTypeCharge: Util.Float2(this._order.getProperty("OrderTypeCharge")),
                Discount: Util.Float2(this._order.getProperty("Discount")),
                Donations: donations,
                Donation: donationAmount,
                Gratuity: this._order.getProperty("Tip") || 0.00,
                IsPosted: false,
                PostedTime: null,
                PostedByID: null,
                PostedStationID: null,
                StatusID: statusId,
                StatusTime: Util.formatDateTimeSeconds(now),
                StatusByID: 0,
                DispatchID: null,
                FundraiserID: this._order.getProperty("FundraiserID"),
                Fundraiser: null,
                IsTaxExempt: false,
                IsPrePaid: false,
                IsLocked: false,
                IsPriority: false,
                SplitCheckCount: 0,
                GuestCount: 0,
                Distance: this._order.getProperty("Distance"),
                PriceGroupID: this._order.getProperty("OrderTypeSubType").PriceGroupId,
                Contactless: (this._modules.locationSection.contactless || this._item.curbsideInfoIsCurbside || false),
                SaveToken: false,

                Challenges: null,
                Comments: null,
                Coupons: null,
                ServiceCharges: null,
                Customer: null,
                Address: null,
                OrderItems: null,
                ItemsComments: null,
                ItemsModifiers: null,
                ItemsTaxes: null,
                ItemsMakeList: null,
                ItemsCoupons: null,
                Taxes: null,
                Payments: [],
                PaymentsDetail: [], // data for the detail will be populated on server side
                RedemptionIDs: [],

                Notify: false,
                NotifyEmail: null,
                NotifyPhone: null,
                NotifyCountryCodeId: null,
                CustomerProfile: ""
            };

            if (Util.isAppView()) {
                const appPlatform = this._context.getProperty("platform");
                nOrder.SourceID = Util.isiOS(appPlatform) ? 4 : 3;
            }

            return nOrder;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteOrder", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _getFuturePrintTime = (futureDate: Date, promiseTime: Date, futurePrintTimeSetting: string, waitTime: number): string => {
        const pTime = futurePrintTimeSetting || "0";
        let printTime;

      try {
            const prnTime = new Date(Util.formatDateTime(Util.DateAdd(promiseTime, -waitTime, "m"), "en-US"));
            printTime = prnTime instanceof Date && pTime == "0" ?
                Util.formatDateTimeSeconds(prnTime) :
                Util.formatDateTimeSeconds(new Date(`${Util.formatDate(futureDate, "en-US")} ${pTime}`));
        } catch (ex) {
            printTime = Util.formatDateTimeSeconds(Util.DateAdd(futureDate, 540, "m"));
        }
        try {
            if (printTime.indexOf('Invalid') > -1)
                printTime = Util.formatDateTime(Util.DateAdd(promiseTime, -waitTime, "m"), "en-US");
        } catch {
            printTime = Util.formatDateTime(Util.DateAdd(promiseTime, -60, "m"), "en-US");
        }

        return printTime;
    }

    private readonly _completeDonations = (): InOrderDonation[] => {
        const donations: InOrderDonation[] = [];
        this._order.getProperty("Donations").Detail.forEach(donation => {
            if (donation.Amount > 0) {
                donations.push({
                    DonationID: donation.ID,
                    Amount: donation.Amount
                });
            }
        });
        return donations;
    }

    private readonly _completeItemsModifiersTaxesComments = (): InOrderItemInfo | null => {
        try {
            const nonePreModifierId = this._data.getProperty("NonePreModifierId");

            const items: InOrderItem[] = [];
            const comments: InOrderItemComment[] = [];
            const modifiers: InOrderItemModifier[] = [];
            const taxes: InOrderItemTax[] = [];

            for (const oItem of this._order.getProperty("Items")) {
                let price = oItem.Price;
                if (oItem.PriceIncludesTax)
                    price = Util.Float2(price - oItem.Tax);

                const iItem: InOrderItem = {
                    IsNew: true,
                    ItemRecID: oItem.ItemRecId,
                    ItemKey: oItem.ItemKey,
                    HalfIndex: oItem.HalfIndex,
                    ItemID: oItem.ItemId,
                    Name: oItem.Name,
                    SizeID: oItem.SizeId,
                    SizeNames: oItem.SizeNames,
                    HalfCount: oItem.HalfCount,
                    Quantity: Util.Float5(oItem.Quantity),
                    DiscountedQuantity: oItem.DiscountedQuantity,
                    Price: price,
                    UnitPrice: oItem.MenuPrice,
                    HalfHalfPrice: oItem.HalfHalfPrice,
                    BeforeTaxDiscount: Util.formatNumber(oItem.BeforeTaxDiscount),
                    AfterTaxDiscount: Util.formatNumber(oItem.AfterTaxDiscount),
                    GuestNo: 0,
                    CheckNo: 0,
                    IsAdded: false,
                    AddedByID: null,
                    AddedTime: null,
                    IsRemoved: false,
                    RemovedByID: null,
                    RemovedTime: null,
                    IsPrinted: false,
                    Tax: oItem.Tax,
                    PriceIncludesTax: oItem.PriceIncludesTax,
                    MenuPriceTax: oItem.MenuPriceTax
                };

                items.push(iItem);

                if (oItem.Comment !== "")
                    comments.push({
                        TypeID: 2,
                        ItemKey: oItem.ItemKey,
                        HalfIndex: oItem.HalfIndex,
                        ItemRecID: oItem.ItemRecId,
                        Comment: oItem.Comment,
                        CommentTime: Util.NowStore(this._data.getProperty("TimeOffset")),
                        CommentByID: 0
                    });

                if (oItem.Instructions.length > 0) {
                    let inst = "";
                    const storeCultureCode = this._data.getProperty("StoreInfo").country.DefaultCultureCode ?? "en-US";
                    for (const instruction of oItem.Instructions) {
                        const tmpInstruction = this._data.getProperty("Instructions").find(x => x.CommentId === instruction.commentId);
                        if (tmpInstruction)
                            inst += `${this._localizationService.GetNewName(tmpInstruction.Names, storeCultureCode)}, `;
                    }
                    if (oItem.Comment === '') {
                        comments.push({
                            TypeID: 2,
                            ItemKey: oItem.ItemKey,
                            HalfIndex: oItem.HalfIndex,
                            ItemRecID: oItem.ItemRecId,
                            Comment: inst.slice(0, -2),
                            CommentTime: Util.NowStore(this._data.getProperty("TimeOffset")),
                            CommentByID: 0
                        });
                    } else
                        comments[0].Comment += `, ${inst.slice(0, -2)}`;
                }

                const disGrpModCount: Record<number, number> = [];
                for (const mod of oItem.Modifiers) {
                    let modCnt;
                    if (disGrpModCount[mod.MergedModifierDisplayGroupId]) {
                        modCnt = disGrpModCount[mod.MergedModifierDisplayGroupId];
                    } else {
                        disGrpModCount[mod.MergedModifierDisplayGroupId] = OnlineOrderingUtil.GetModCount(oItem.Modifiers, mod.MergedModifierDisplayGroupId, nonePreModifierId);
                        modCnt = disGrpModCount[mod.MergedModifierDisplayGroupId];
                    }
                    const nMod: InOrderItemModifier = {
                        ItemKey: oItem.ItemKey,
                        HalfIndex: oItem.HalfIndex,
                        ItemRecID: oItem.ItemRecId,
                        ModifierID: mod.ModifierId,
                        Name: mod.Name,
                        PreModifierID: mod.PreModifierId,
                        PriceGroupCount: this._data.getProperty("PreModifiers").find(x => x.PreModifierId === mod.PreModifierId)?.PriceCountMultiplier,
                        ModCnt: modCnt,
                        Price: mod.IsDefault ? 0 : mod.Price
                    };
                    modifiers.push(nMod);
                }

                for (const tax of oItem.Taxes) {
                    const nTax: InOrderItemTax = {
                        TaxID: tax.TaxId,
                        ItemKey: oItem.ItemKey,
                        HalfIndex: oItem.HalfIndex,
                        ItemRecID: oItem.ItemRecId,
                        TaxAmount: tax.Amount
                    };
                    taxes.push(nTax);
                }
            }

            return {
                Modifiers: modifiers,
                Comments: comments,
                Items: items,
                Taxes: taxes
            };
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteItemsModifiersTaxesComments", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _completeChallenges = (): InOrderChallenge[] | null => {
        const orderChallenges = this._order.getProperty("Challenges");
        if (!orderChallenges || !orderChallenges?.length)
            return null;

        const challenge = orderChallenges[0];
        const challenges: InOrderChallenge[] = [];
        for (const member of challenge.members) {
            if (member.events.length > 0) {
                for (const event of member.events) {
                    challenges.push({
                        challengeId: challenge.challengeId,
                        memberId: member.memberId,
                        eventId: event.eventId,
                        subMember: event.subMember,
                        subMemberId: event.subMemberId,
                        extra: event.extra
                    });
                }
            } else {
                challenges.push({
                    challengeId: challenge.challengeId,
                    memberId: member.memberId,
                    eventId: null,
                    subMember: null,
                    subMemberId: null,
                    extra: null
                });
            }
        }

        return challenges;
    }

    private readonly _completeComments = (): InOrderComment[] | null => {
        try {
            const now = Util.NowStore(this._data.getProperty("TimeOffset"));
            const appComment = Util.getElementValue("txt_APP_CHKOUT_Comment").trim();
            const comments: InOrderComment[] = [];
            // The OrderHub checkout has no comment input, so fall back to the comment already on the order.
            const checkoutComment = (this._context.getProperty("isOrderHub") && !document.getElementById("txt_checkout_comment"))
                ? (this._order.getProperty("Comment") ?? "").trim()
                : this._item.comments.trim();
            let orderComment = checkoutComment + appComment;

            if (this._data.getProperty("Settings").OOS.togoSupplies.length > 0 && !this._context.getProperty("qrCode")) {
                const tgsInitComment = "To-Go Utensils: ";

                let tgsBodyComment = "";
                for (const item of this._item.toGoItems) {
                    tgsBodyComment += tgsBodyComment === "" ? item.name : `, ${item.name}`;
                }

                if (tgsBodyComment !== "") {
                    if (orderComment !== "")
                        orderComment += ". ";
                    orderComment += tgsInitComment + tgsBodyComment;
                }
            }

            if (orderComment !== "") {
                const comment: InOrderComment = {
                    CommentTypeID: 3,
                    CommentTime: Util.formatDateTimeSeconds(now),
                    CommentByID: 0,
                    Comment: orderComment
                };
                comments.push(comment);
            }

            let cComment = "";
            if (this._modules.locationSection.contactless)
                cComment = "  CONTACTLESS DELIVERY  ";
            else if (this._item.curbsideInfoIsCurbside)
                cComment = "    CURBSIDE PICKUP     ";

            if (cComment !== "") {
                const comment: InOrderComment = {
                    CommentTypeID: 12,
                    CommentTime: Util.formatDateTimeSeconds(now),
                    CommentByID: 0,
                    Comment: cComment
                };
                comments.push(comment);
            }

            if (this._item.curbsideInfoIsCurbside && this._data.getProperty("Settings").CURBSD == Curbside.CUSTOMER_CAR_INFO) {
                const carComment = this._item.curbsideInfoCarText;
                if (carComment !== "") {
                    const comment: InOrderComment = {
                        CommentTypeID: 13,
                        CommentTime: Util.formatDateTimeSeconds(now),
                        CommentByID: 0,
                        Comment: carComment
                    };
                    comments.push(comment);
                }
            }

            return comments;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteComments", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _completeCoupons = (): InOrderCoupon[] | null => {
        try {
            const coupons: InOrderCoupon[] = [];
            for (const coupon of this._order.getProperty("Coupons")) {
                const iCoupon: InOrderCoupon = {
                    CouponKey: coupon.CouponKey,
                    CouponID: coupon.CouponId,
                    CouponCode: coupon.CouponCode,
                    Amount: Util.Float2(coupon.Amount),
                    IsActive: coupon.IsActive,
                    IsAdded: coupon.IsAdded,
                    AddedByID: null,
                    AddedTime: null,
                    IsRemoved: coupon.IsRemoved,
                    RemovedByID: null,
                    RemovedTime: null,
                    ChannelID: coupon.ChannelID,
                    IsCampaignCode: coupon.IsCampaignCode,
                    IsPromotionCode: coupon.IsPromotionCode,
                    IsSingleUseCoupon: (coupon.IsCampaignCode
                        || !!this._user.getProperty("Offers")?.Codes.find(x => x.CouponCode == coupon.CouponCode.toUpperCase() && x.IsSingleUseCoupon)
                        || !!coupon.IsSingleUseCoupon),
                    RewardId: coupon.RewardId,
                    IsBankedCurrency: coupon.IsBankedCurrency,
                    RedemptionReference: coupon.RedemptionReference,
                    BasketItemId: coupon.BasketItemId,
                    SourceId: coupon.SourceId,
                    Discount: null
                };
                coupons.push(iCoupon);
            }
            return coupons;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteCoupons", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _completeServiceCharges = (): InOrderServiceCharge[] => {
        const charges: InOrderServiceCharge[] = [];
        for (const oCharge of this._order.getProperty("ServiceCharges").Detail) {
            const charge: InOrderServiceCharge = {
                ServiceChargeID: oCharge.ID,
                Amount: oCharge.Amount,
                Overwritten: oCharge.Overwritten,
                TaxAmount: oCharge.Tax
            };
            charges.push(charge);
        }
        return charges;
    }

    private readonly _completeCustomer = (): InOrderCustomer | null => {
        try {
            const customer: InOrderCustomer = {
                CustomerID: 0,
                CustomerTypeID: 1,
                Name: this._item.contactInfoFirstName.trim(),
                LastName: this._item.contactInfoLastName.trim(),
                Email: this._item.contactInfoEmail.trim(),
                ProfileID: 0,
                storeID: this._data.getProperty("StoreInfo").storeId,
                PriceGroupID: this._order.getProperty("OrderTypeSubType").PriceGroupId,
                CustomerGroupID: this._data.getProperty("Settings").CGID,
                Phone: Util.cleanNonDigits(this._item.contactInfoPhone),
                CountryCodeId: Number(this._item.contactInfoCountryCodeId)
            };

            if (this._user.isLoggedIn()) {
                customer.CustomerID = this._user.getProperty("CustomerId") || 0;
                customer.Name = this._user.getProperty("Name") || "";
                customer.LastName = this._user.getProperty("LastName") || "";
                customer.Email = this._user.getProperty("Email") || "";
                customer.ProfileID = this._user.getProperty("ProfileId") || 0;
                customer.storeID = this._user.getProperty("StoreId") || 0;
                customer.CountryCodeId = this._user.getProperty("CountryCodeId") || 1;

                customer.PriceGroupID = this._order.getProperty("OrderTypeSubType").PriceGroupId;
                customer.CustomerGroupID = this._data.getProperty("Settings").CGID;

                const phone = this._user.getProperty("Phone");
                if (phone)
                    customer.Phone = Util.cleanNonDigits(phone);
            }
            return customer;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteCustomer", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _completeAddress = (): InOrderAddress | null => {
        try {
            const address = this._order.getProperty("Address");
            if (this._order.getProperty("OrderTypeID") !== OrderType.DELIVERY || !address)
                return null;

            const nAddr: InOrderAddress = {
                AddressID: address.AddressID,
                AddressTypeID: address.AddressTypeID,
                AddressName: address.AddressName,
                IsPrimary: address.IsPrimary,
                StreetNo: address.StreetNo,
                StreetName: address.StreetName,
                Address1: address.StreetName,
                Address2: address.Address2,
                Address3: address.Address3,
                Address4: address.Address4,
                Address5: address.Address5,
                City: address.City,
                State: address.State,
                Zip: address.Zip,
                CountryID: address.CountryID,
                Latitude: address.Latitude,
                Longitude: address.Longitude,
                XStreet: address.XStreet,
                ZoneID: address.ZoneID,
                Grid: address.Grid,
                Distance: address.Distance,
                DeliveryInstruction: address.DeliveryInstruction,
                CustomerID: this._user.getProperty("CustomerId") || 0
            };
            return nAddr;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteAddress", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _completeMakeList = (promiseTime: string): InOrderMakeList[] => {
        let makeList: InOrderMakeList[] = [];

        try {
            const printTime = new Date(promiseTime);
            let extOrderNo = 0;
            if (!this._order.getProperty("IsNew"))
                extOrderNo = this._getLastExtendedOrderNo();

            for (const oItem of this._order.getProperty("Items")) {
                const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId);
                if (!mItem)
                    continue;

                const iMake = this._checkItemPrintMakeLblCutPrp(mItem, this._order.getProperty("OrderTypeSubType").OrderTypeId);
                const tmpList = this._createMakeList(oItem, this._order.getProperty("Items"), iMake, 1, oItem.Quantity, extOrderNo, printTime);
                if (tmpList) {
                    extOrderNo = tmpList.ExtendedOrderNo;
                    makeList = makeList.concat(tmpList.MakeList);
                }
            }
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteMakeList", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }

        return makeList;
    }

    private readonly _getLastExtendedOrderNo = (): number => {
        let maxExtendedOrderNo = 0;
        try {
            for (const item of this._order.getProperty("ItemsMakeList")) {
                if (Number(item.XONO) > maxExtendedOrderNo)
                    maxExtendedOrderNo = Number(item.XONO);
            }
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("GetLastExtendedOrderNo", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return maxExtendedOrderNo;
    }

    private readonly _checkItemPrintMakeLblCutPrp = (mItem: IDataItem, orderTypeId: number): IMake => {
        const iMake: IMake = {
            Prn: false,
            Mak: false,
            Lbl: false,
            Prp: false,
            Cwp: false
        };

        for (const ot of mItem.OrderTypes) {
            if (ot.OrderTypeId == orderTypeId && (ot.Print || ot.Make || ot.Label || ot.CutWrap || ot.Prep)) {
                iMake.Prn = (iMake.Prn === true) ? iMake.Prn : ot.Print;
                iMake.Mak = (iMake.Mak === true) ? iMake.Mak : ot.Make;
                iMake.Lbl = (iMake.Lbl === true) ? iMake.Lbl : ot.Label;
                iMake.Prp = (iMake.Prp === true) ? iMake.Prp : ot.Prep;
                iMake.Cwp = (iMake.Cwp === true) ? iMake.Cwp : ot.CutWrap;
            }
        }

        return iMake;
    }

    private readonly _createMakeList = (oItem: IOrderItem, oItems: IOrderItem[], iMake: IMake, QTYStart: number, QTYEnd: number, extOrderNo: number, printTime: Date): ICreateMakeList | null => {
        try {
            const makeList: InOrderMakeList[] = [];
            const lastItemKey = 0;
            const iExtOrderNo = extOrderNo;

            const itemIds = [... new Set(oItems.filter(x => x.ItemKey == oItem.ItemKey).map(x => x.ItemId))];
            const mItems = this._data.getProperty("Items").filter(x => itemIds.includes(x.ItemId));

            let prepTime = 0;
            let makeTime = 0;
            let cookTime = 0;

            for (const mItem of mItems) {
                if (mItem.PrepTime > prepTime)
                    prepTime = mItem.PrepTime;

                if (mItem.MakeTime > makeTime)
                    makeTime = mItem.MakeTime;

                if (mItem.CookTime > cookTime)
                    cookTime = mItem.CookTime;
            }

            for (let i = QTYStart; i <= QTYEnd; i++) {
                if (lastItemKey != oItem.ItemKey)
                    extOrderNo += 1;

                const makeItem: InOrderMakeList = {
                    IsNew: true,
                    ItemKey: oItem.ItemKey,
                    HalfIndex: oItem.HalfIndex,
                    ItemRecID: oItem.ItemRecId,
                    QuantityIndex: i,
                    ExtendedOrderNo: extOrderNo,
                    ItemID: oItem.ItemId,
                    HalfCount: oItem.HalfCount,
                    IsPrinted: false,

                    PrepStartTime: null,
                    PrepEndTime: null,
                    MakeStartTime: null,
                    MakeEndTime: null,
                    CookStartTime: null,
                    CookEndTime: null,

                    IsAdded: false,
                    AddedByID: null,
                    AddedTime: null,
                    IsRemoved: false,
                    RemovedByID: null,
                    RemovedTime: null
                };

                // Prep Station
                let bypassPrepStation = false;
                if (!this._data.getProperty("Settings").HPRP || !iMake.Prp) {
                    bypassPrepStation = true;
                    makeItem.PrepStartTime = Util.formatDateTimeSeconds(printTime);
                    makeItem.PrepEndTime = Util.formatDateTimeSeconds(Util.DateAdd(printTime, prepTime, "m"));
                }

                // Make Line
                let bypassMakeLine = false;
                if (bypassPrepStation && (!this._data.getProperty("Settings").HML || !this._data.getProperty("Settings").HMLM || !iMake.Mak)) {
                    bypassMakeLine = true
                    makeItem.MakeStartTime = Util.formatDateTimeSeconds(Util.DateAdd(printTime, prepTime, "m"));
                    makeItem.MakeEndTime = Util.formatDateTimeSeconds(Util.DateAdd(printTime, prepTime + makeTime, "m"));
                    makeItem.CookStartTime = Util.formatDateTimeSeconds(Util.DateAdd(printTime, prepTime + makeTime, "m"));
                }

                // Cut & Wrap
                if (bypassMakeLine && (!this._data.getProperty("Settings").HCW || !this._data.getProperty("Settings").HCWM || !iMake.Cwp)) {
                    makeItem.CookEndTime = Util.formatDateTimeSeconds(Util.DateAdd(printTime, prepTime + makeTime + cookTime, "m"));
                }

                makeList.push(makeItem);
            }

            if (oItem.HalfCount > 1 && oItem.HalfCount != oItem.HalfIndex)
                extOrderNo = iExtOrderNo;

            return {
                MakeList: makeList,
                ExtendedOrderNo: extOrderNo
            };
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CreateMakeList", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _completeItemsCoupons = (): IOrderItemCoupon[] | null => {
        try {
            const itemCoupons: IOrderItemCoupon[] = [];

            for (const coupon of this._order.getProperty("ItemsCoupons")) {
                if (!this._isValidItemKey(this._order.getProperty("Items"), coupon.ItemKey))
                    continue;

                const itemCoupon: IOrderItemCoupon = {
                    ItemKey: coupon.ItemKey,
                    HalfIndex: coupon.HalfIndex,
                    ItemRecId: coupon.ItemRecId,
                    CouponKey: coupon.CouponKey,
                    QTYIndex: coupon.QTYIndex,
                    BeforeTaxDiscount: Util.Float2(coupon.BeforeTaxDiscount),
                    AfterTaxDiscount: Util.Float2(coupon.AfterTaxDiscount),
                    DiscountedQuantity: coupon.DiscountedQuantity,
                    IsActive: coupon.IsActive,
                    Priority: coupon.Priority
                };
                itemCoupons.push(itemCoupon);
            }
            return itemCoupons;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteItemsCoupos", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _isValidItemKey = (orderItems: IOrderItem[], itemKey: number): boolean => {
        return orderItems.some(oItem => oItem.ItemKey === itemKey);
    }

    private readonly _completeTaxes = (): ITax[] | null => {
        try {
            const taxes: ITax[] = [];
            for (const oTax of this._order.getProperty("Taxes")) {
                const tax: ITax = {
                    TaxTypeID: oTax.TaxTypeId,
                    TaxAmount: oTax.Amount
                };
                taxes.push(tax);
            }
            return taxes;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteTaxes", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return null;
    }

    private readonly _completePayments = (): IOrderPayment[] => {
        try {
            const giftPayments = this._completeGiftCardPayments();
            let payments: IOrderPayment[] = [];
            const tip = this._calculateNonGiftTip(giftPayments);
            const amountDue = this._order.getProperty("AmountDue");
            if (amountDue > 0) {
                const newPay = this._buildBasePayment(amountDue, tip, giftPayments);
                this._applyPaymentOption(newPay, payments);
            }
            if (giftPayments.length > 0)
                payments = payments.concat(giftPayments);
            return payments;
        } catch {
            this._item.isSubmitting = false;
        }
        return [];
    }

    private _calculateNonGiftTip(giftPayments: IOrderPayment[]): number {
        let tip = this._order.getProperty("Tip");
        if (tip > 0) {
            const totalGiftCardTips = giftPayments?.reduce((acc, curr) => acc + curr.Gratuity, 0) || 0;
            tip -= totalGiftCardTips;
        }
        return tip;
    }

    private _buildBasePayment(amountDue: number, tip: number, giftPayments: IOrderPayment[]): IOrderPayment {
        const merchant = this._getEcommerceMerchant();
        const newPay = this._getEmptyPayment();
        newPay.PaymentKey = giftPayments ? giftPayments.length : 0;
        newPay.PaymentTypeID = 1;
        newPay.Amount = amountDue;
        newPay.TaxAmount = this._order.getProperty("Tax").toFixed(2);
        newPay.Gratuity = tip;
        if (tip > 0) {
            newPay.GratuityByID = 0;
            newPay.GratuityTime = Util.formatDateTimeSeconds(Util.NowStore(this._data.getProperty("TimeOffset")));
        }
        newPay.CurrencyAmount = amountDue;
        newPay.Balance = amountDue;
        newPay.SaveToken = this._item.creditCardInfoSaveToken;
        newPay.IsOmniToken = merchant?.isOmniToken ?? false;
        newPay.ProcessorId = merchant?.ProcessorId ?? 1;
        newPay.BillingAddress = "";
        newPay.BillingZip = "";
        return newPay;
    }

    private _applyPaymentOption(newPay: IOrderPayment, payments: IOrderPayment[]): void {
        // This (and its uses) goes away after Bahamas fix is no longer needed
        const creditCardOnDelivery = this._item.paymentOptionId === OnlinePaymentOption.CREDIT_CARD_UPON_DELIVERY;
        const workaround = creditCardOnDelivery && this._data.getProperty("StoreInfo").brandId === "A5985C38-D485-4240-8C01-931F7186D567";
        if (this._item.paymentOptionId === OnlinePaymentOption.PAY_IN_STORE || workaround) return;

        [
            () => this._buildCashPayment(newPay, payments),
            () => this._buildCreditCardOnDeliveryPayment(newPay, payments),
            () => this._buildWalletPayment(newPay, payments),
            () => this._buildCreditCardPayment(newPay, payments),
            () => this._buildApplePayPayment(newPay, payments),
            () => this._buildGooglePayPayment(newPay, payments),
        ].some(h => h());
    }

    private _buildCashPayment(newPay: IOrderPayment, payments: IOrderPayment[]): boolean {
        if (this._item.paymentOptionId !== OnlinePaymentOption.CASH) return false;
        newPay.PaymentTypeID = PaymentPaymentType.CASH;
        payments.push(newPay);
        return true;
    }

    private _buildCreditCardOnDeliveryPayment(newPay: IOrderPayment, payments: IOrderPayment[]): boolean {
        if (this._item.paymentOptionId !== OnlinePaymentOption.CREDIT_CARD_UPON_DELIVERY) return false;
        newPay.PaymentTypeID = PaymentPaymentType.CREDIT_CARD_ON_DELIVERY;
        payments.push(newPay);
        return true;
    }

    private _buildWalletPayment(newPay: IOrderPayment, payments: IOrderPayment[]): boolean {
        if (this._item.paymentOptionId !== OnlinePaymentOption.CREDIT_CARD_WALLET || this._item.customerWalletCard === null || !this._user.isLoggedIn())
            return false;
        const wallet = this._getWalletForGivenStore();
        if (!wallet) return false;
        const card = wallet[this._item.customerWalletCard];
        newPay.PaymentTypeID = PaymentPaymentType.CREDIT_CARD_WALLET;
        newPay.TransData = this._getInfo(true, card);
        newPay.BillingAddress = card.billingAddress.trim().substring(0, 50);
        newPay.BillingZip = card.billingZip;
        newPay.ExpMnt = card.expirationMonth;
        newPay.ExpYr = card.expirationYear;
        payments.push(newPay);
        return true;
    }

    private _buildCreditCardPayment(newPay: IOrderPayment, payments: IOrderPayment[]): boolean {
        if (this._item.paymentOptionId !== OnlinePaymentOption.CREDIT_CARD)
            return false;
        newPay.PaymentTypeID = PaymentPaymentType.CREDIT_CARD;
        if (this._data.getProperty("Settings").CCI)
            this._applyCreditCardDetails(newPay);
        payments.push(newPay);
        return true;
    }

    private _applyCreditCardDetails(newPay: IOrderPayment): void {
        newPay.TransData = this._getInfo(false, null);
        newPay.BillingAddress = this._item.creditCardInfoAddress.trim().substring(0, 50);
        newPay.BillingZip = this._item.creditCardInfoZip.trim();
        if (!this._isHeartland()) {
            const expiration = this._item.creditCardInfoExpirationDate.trim().split("/");
            newPay.ExpMnt = expiration[0];
            newPay.ExpYr = expiration[1];
        }
        if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants")))
            this._applyOpenPayMerchantData(newPay);
        else if (this._isO2G()) {
            // 2DO: Waiting on O2G to do stuff
            //let orderDetails: any = [];
            //for (const item of order.Items) {
            //    var orderDetail: any = {};
            //    orderDetail.Item = OnlineOrderingUtil.getNamByCulture(item.Name, 'en-us');
            //    orderDetail.ServerId = "0";
            //    orderDetail.AllPrice = item.Price.toFixed(2);
            //    orderDetail.Price = item.Price.toFixed(2);
            //    orderDetail.Qty = item.Quantity;
            //    orderDetail.TaxMv = item.Tax.toFixed(2);
            //    orderDetail.Waiter = "0";
            //    orderDetails.push(orderDetail);
            //}
            //const mercSpecData = {
            //    firstName: Util.getElementValue("txt_checkout_name") ? Util.getElementValue("txt_checkout_name") : this._user.getProperty("Name"),
            //    lastName: Util.getElementValue("txt_checkout_last_name") ? Util.getElementValue("txt_checkout_last_name") : this._user.getProperty("LastName"),
            //    phoneNumber: Util.getElementValue("txt_checkout_phone") ? Util.getElementValue("txt_checkout_phone") : this._user.getProperty("Phone"),
            //    email: Util.getElementValue("txt_checkout_email") ? Util.getElementValue("txt_checkout_email") : this._user.getProperty("Email"),
            //    items: orderDetails,
            //    tax: this._order.getProperty("Tax").toFixed(2),
            //}
            //newPay.MerchantSpecData = JSON.stringify(mercSpecData);
        }
    }

    private _applyOpenPayMerchantData(newPay: IOrderPayment): void {
        const isLoggedIn = this._user.isLoggedIn();
        const customer = {
            name: isLoggedIn ? this._user.getProperty("Name") : this._item.contactInfoFirstName,
            last_name: isLoggedIn ? this._user.getProperty("LastName") : this._item.contactInfoLastName,
            phone_number: isLoggedIn ? this._user.getProperty("Phone") : this._item.contactInfoPhone,
            email: isLoggedIn ? this._user.getProperty("Email") : this._item.contactInfoEmail
        };
        newPay.MerchantSpecData = JSON.stringify({
            devSId: this._data.getProperty("OPDevSId"),
            sourceId: this._data.getProperty("OPSourceId"),
            currency: "MXN", //TODO: check out how it is done after latest change for MX
            customer
        });
    }

    // Carried in DigitalWalletData, not TransData -- see that field's comment in order.interface.ts
    // for why the two are kept separate.
    private _buildDigitalWalletData(walletType: WalletType, walletToken: string): string {
        const processorId = this._getEcommerceMerchant()?.ProcessorId;

        if (processorId === MerchantProcessor.Heartland) {
            return JSON.stringify({ WLTYP: this._getHeartlandWalletType(walletType), WLTKN: walletToken, WLAPP: this._isMobileAppRequest() });
        }
        if (walletType === WalletType.ApplePay && processorId !== MerchantProcessor.WorldPayTriPOS)
            return walletToken;

        return JSON.stringify({ WLTYP: walletType, WLTKN: walletToken });
    }

    private _getHeartlandWalletType(walletType: WalletType): WalletType {
        const isMobile = this._isMobileAppRequest();

        const walletTypeMap: Partial<Record<WalletType, { app: WalletType; web: WalletType }>> = {
            [WalletType.GooglePay]: { app: WalletType.GooglePayApp, web: WalletType.GooglePayWeb },
            [WalletType.ApplePay]: { app: WalletType.ApplePayApp, web: WalletType.ApplePayWeb },
        };

        const mapping = walletTypeMap[walletType];
        if (!mapping) {
            return WalletType.None;
        }

        return isMobile ? mapping.app : mapping.web;
    }

    private _buildApplePayPayment(newPay: IOrderPayment, payments: IOrderPayment[]): boolean {
        if (this._item.paymentOptionId !== OnlinePaymentOption.APPLE_PAY || !this._item.applePayToken)
            return false;
        newPay.PaymentTypeID = PaymentPaymentType.CREDIT_CARD;
        newPay.DigitalWalletData = this._buildDigitalWalletData(WalletType.ApplePay, this._item.applePayToken);
        newPay.TransData = this._getInfo(false, null);
        payments.push(newPay);
        return true;
    }

    private _buildGooglePayPayment(newPay: IOrderPayment, payments: IOrderPayment[]): boolean {
        if (this._item.paymentOptionId !== OnlinePaymentOption.GOOGLE_PAY || !this._item.googlePayToken)
            return false;
        newPay.PaymentTypeID = PaymentPaymentType.CREDIT_CARD;
        newPay.DigitalWalletData = this._buildDigitalWalletData(WalletType.GooglePay, this._item.googlePayToken);
        newPay.TransData = this._getInfo(false, null);
        payments.push(newPay);
        return true;
    }

    private readonly _isO2G = (): boolean => {
        return this._data.getProperty("Merchants").some(x =>
            x.ProcessorId === MerchantProcessor.O2GPay && x.TransactionType.toUpperCase().includes('E')
        ) ?? false;
    }

    private readonly _completeGiftCardPayments = (): IOrderPayment[] => {
        try {
            const payments: IOrderPayment[] = [];
            let totalGiftCardPayment = 0;
            let totalGiftCardTip = 0;

            for (const [index, giftCard] of this._order.getProperty("GiftCards").entries()) {
                const newPay = this._getEmptyPayment();
                newPay.PaymentKey = index;
                newPay.PaymentTypeID = 7;
                newPay.Amount = giftCard.appliedAmount;
                newPay.CurrencyAmount = giftCard.appliedAmount;
                newPay.AccountID = giftCard.cardNo;
                newPay.Balance = giftCard.balance - giftCard.appliedAmount;
                newPay.PIN = giftCard.pin;
                newPay.TaxAmount = '0';
                if (this._order.getProperty("Tip") > 0) {
                    const tipAppliedToThisCard = Util.Float2(giftCard.appliedAmount - this._order.getProperty("Total") + totalGiftCardPayment + this._order.getProperty("Tip") - totalGiftCardTip);
                    if (tipAppliedToThisCard > 0) {
                        totalGiftCardTip += tipAppliedToThisCard;
                        newPay.Gratuity = tipAppliedToThisCard;
                        newPay.GratuityByID = 0;
                        newPay.GratuityTime = Util.formatDateTimeSeconds(Util.NowStore(this._data.getProperty("TimeOffset")));
                    }
                }
                totalGiftCardPayment += giftCard.appliedAmount;
                payments.push(newPay);
            }
            return payments;
        } catch (ex: unknown) {
            // if (ex instanceof Error)
            //     Util.LogError("CompleteGiftCardPayments", ex, globalThis.aOLO);

            this._item.isSubmitting = false;
        }
        return [];
    }

    private readonly _getEmptyPayment = (): IOrderPayment => {
        return {
            PaymentKey: 0,
            PaymentTypeID: 1,
            Amount: 0,
            TaxAmount: "",
            ChangeDue: 0,
            Gratuity: 0,
            GratuityByID: null,
            GratuityTime: null,
            CurrencyID: 140,
            CurrencyAmount: 0,
            CurrencyChangeDue: 0,
            CurrencyExRate: 1,
            IsAdded: false,
            AddedByID: null,
            AddedTime: null,
            IsRemoved: false,
            RemovedByID: null,
            RemovedTime: null,

            AccountID: "",
            ExpDate: "",
            Balance: 0,
            Reference: "",
            MerchantAdoraID: 0,
            StatusID: 1,
            TransData: "",
            Token: "",
            CVM: "",
            PIN: "",
            SaveToken: false,
            BillingAddress: "",
            BillingZip: "",
            ExpMnt: "",
            ExpYr: "",
            MerchantSpecData: "",
            IsOmniToken: false,
            ProcessorId: 1
        };
    }

    private readonly _getInfo = (exCard: boolean, card: IUserCard | null): string => {
        const pData = {
            TKN: "",
            LST4: "",
            BADR: this._item.creditCardInfoAddress.trim(),
            BZIP: this._item.creditCardInfoZip.trim(),
            CCTID: "",
            CNO: "",
            EXPMNT: "",
            EXPYR: "",
            CVV: "",
            MCHSPEC: ""
        };

        if (exCard) {
            let cvv = ""
            const wallet = this._user.getProperty("Wallet");
            if (wallet && this._item.customerWalletCard !== null)
                cvv = Util.getElementValue(`txt_checkout_payment_wallet_cvv_${this._item.customerWalletCard}`).trim();

            pData.TKN = card?.token || "";
            pData.LST4 = card?.last4 || "";
            pData.BADR = card?.billingAddress || "";
            pData.BZIP = card?.billingZip || "";
            pData.CCTID = card?.cardTypeId?.toString() || "";
            pData.EXPMNT = card?.expirationMonth || "";
            pData.EXPYR = card?.expirationYear || "";
            pData.MCHSPEC = card?.merchantSpecData || "";
            pData.CVV = cvv;
        } else if (this._isHeartland()) {
            pData.TKN = this._context.getProperty("heartland").token || "";
            pData.LST4 = this._context.getProperty("heartland").last4 || "";
        } else {
            const expiration = this._item.creditCardInfoExpirationDate.trim().split("/");
            pData.CNO = Util.cleanNonDigits(this._item.creditCardInfoCardNumber.trim());
            pData.EXPMNT = expiration[0];
            pData.EXPYR = expiration[1];
            pData.CVV = this._item.creditCardInfoCvv;
        }

        return JSON.stringify(pData);
    }

    private readonly _openPaySubmitAsync = async (): Promise<void> => {
        if (this._order.getProperty("PaymentType") === PaymentType.PAY_IN_STORE) {
            await this._submitAsync(false);
        } else {
            this._openPayGetDeviceSourceId();
            this._openPayGetSessionId();
        }
    }

    private readonly _openPayGetDeviceSourceId = (): void => {
        //@ts-ignore
        OpenPay.setId(this._data.getProperty("OpenPayMerchant").id);
        //@ts-ignore
        OpenPay.setApiKey(this._data.getProperty("OpenPayMerchant").pKey);
        //@ts-ignore
        const info = OpenPay.deviceData.setup("payment-form");
        this._data.setProperty("OPDevSId", info);
    }

    private readonly _openPayGetSessionId = (): void => {
        const openPayMerchant = this._data.getProperty("OpenPayMerchant");
        //@ts-ignore
        OpenPay.setId(openPayMerchant.id);
        //@ts-ignore
        OpenPay.setApiKey(openPayMerchant.pKey);
        //@ts-ignore
        OpenPay.setSandboxMode(this._context.getProperty("developmentMode"));
        //@ts-ignore
        OpenPay.token.extractFormAndCreate('payment-form', this._openPaySuccessCallbackAsync, this._openPayErrorCallback);
    }

    private readonly _openPaySuccessCallbackAsync = async (response: any): Promise<void> => {
        this._data.setProperty("OPSourceId", response.data.id);
        await this._submitAsync(false);
    }

    private readonly _openPayErrorCallback = (response: any): void => {
        const desc = response.data.description != undefined ? response.data.description : response.message;
        DialogCreators.messageBoxOk(
            this._localizationService,
            this._dataLayer,
            desc,
            true
        );
    }

    /**
     * Handles formatting the credit card number input field.
     * Updates the stored credit card number with formatted value.
     * @private
     */
    private readonly _onCreditCardNumberInput = (): void => {
        this._item.creditCardInfoCardNumber = this._formatCreditCard(this._item.creditCardInfoCardNumber);
    }

    /**
     * Handles formatting the expiration date input field.
     * @param {InputEvent} event - The input event triggered by user input.
     * @private
     */
    private readonly _onCreditCardExpirationInput = (event: InputEvent): void => {
        const isDelete = event.inputType === "deleteContentBackward";
        this._item.creditCardInfoExpirationDate = this._formatExpirationDate(this._item.creditCardInfoExpirationDate, isDelete);
    }

    /**
     * Formats a credit card number based on the card type (e.g., Visa, AMEX, Diners).
     * @param {string} creditCardNumber - The unformatted credit card number.
     * @returns {string} The formatted credit card number.
     * @private
     */
    private _formatCreditCard(creditCardNumber: string): string {
        const rawValue = Util.cleanNonDigits(creditCardNumber);
        const cardBrand = Util.getCardBrand(rawValue);
        if (OnlineOrderingUtil.IsOpenPay(this._data.getProperty("Merchants"))) {
            const opTxtCC = document.getElementById("txt_checkout_payment_card_number_op") as HTMLInputElement;
            opTxtCC.value = rawValue;
        }
        let pattern = "#### #### #### ####"; // Default: Visa, MasterCard, etc.

        if (cardBrand === CardBrand.AMEX)
            pattern = "#### ###### #####"; // Amex (4-6-5)
        else if (cardBrand === CardBrand.DINERS)
            pattern = "#### ###### ####"; // Diners Club (4-6-4)

        // Apply formatting
        let formattedValue = "";
        let index = 0;

        for (const char of pattern) {
            if (index >= rawValue.length)
                break; // Stop when all digits are processed

            formattedValue += (char === "#") ? rawValue[index++] : " ";
        }

        return formattedValue.trim();
    }

    /**
     * Formats a credit card expiration date into MM/YY format.
     * @param {string} expirationDate - The current expiration date input value.
     * @param {boolean} isDelete - The current event is a delete.
     * @returns {string} The formatted expiration date.
     * @private
     */
    private _formatExpirationDate(expirationDate: string, isDelete: boolean): string {
        // Allow deletion
        if (isDelete && expirationDate.length <= 2)
            return expirationDate;

        let formattedValue = expirationDate;

        if (expirationDate === "1") {
            formattedValue = "1";
        } else if (expirationDate === "1/") {
            formattedValue = "01/"; // Auto-correct "1/" → "01/"
        } else if (expirationDate.length === 1 && expirationDate !== "0") {
            formattedValue = "0" + expirationDate; // Convert "2" → "02"
        }

        // Ensure MM/YY format
        if (formattedValue.length >= 2) {
            const cleanedValue = Util.cleanNonDigits(formattedValue);
            formattedValue = cleanedValue.slice(0, 2) + "/" + cleanedValue.slice(2, 4);
        }

        return formattedValue;
    }

    private setPaymentOptionsCreditVisible(visible: boolean, id: string) {
        const div = document.getElementById(id);
        if (div) {
            div.hidden = !visible;
        }
    }

    private getExpirationYearsAhead(numYears: number): number[] {
        const currentYear = new Date().getFullYear();
        const expirationYears: number[] = [];

        for (let i = 0; i < numYears; i++) {
            expirationYears.push(currentYear + i);
        }

        return expirationYears;
    }

    private populateExpirationYearsDropdown(numYears: number): void {
        const selectElement = document.getElementById("ddl_checkout_op_credit_entry_expiration_year") as HTMLSelectElement | null;
        if (!selectElement?.parentNode) return;

        selectElement.innerHTML = '<option id="optExpYear0" value="00">Year</option>';

        const expirationYears = this.getExpirationYearsAhead(numYears);

        expirationYears.forEach((year, index) => {
            const optionElement = document.createElement("option");
            optionElement.value = year.toString().slice(-2);
            optionElement.text = year.toString();
            optionElement.id = `optExpYear${index + 1}`;
            selectElement.add(optionElement);
        });
    }

    // private CheckCCValue(): boolean {
    //     const txtCC = document.getElementById("txt_checkout_credit_entry_card_number") as HTMLInputElement;
    //     if (!txtCC)
    //         return false;

    //     const ccValue = txtCC.value;
    //     const ccNumber = Util.cleanNonDigits(ccValue);
    //     const valid = -100; //Refactor: ValidateCardNumber(ccNumber);
    //     if (OnlineOrderingUtil.IsOpenPay(aOLO.data.Merchants)) {
    //         const opTxtCC = document.getElementById("txt_checkout_credit_entry_card_number_OP") as HTMLInputElement;
    //         opTxtCC.value = ccNumber;
    //     }

    //     if (valid > 0) { // it is valid brand and valid number luhn
    //         txtCC.classList.remove("warning");
    //         const expMonth = document.getElementById("ddl_checkout_credit_entry_expiration_month");
    //         if (expMonth)
    //             expMonth.focus();
    //         return true;
    //     } else if (valid == -100) { // it is not valid brand
    //         txtCC.classList.add("warning");
    //         return false;
    //     } else if (valid < 0) { // it is valid brand but invalid number
    //         txtCC.classList.add("warning");
    //         return false;
    //     } else if (valid == 0) { // it is valid brand but number is not complete yet
    //         txtCC.focus();
    //         return false;
    //     }
    //     return false;
    // }


    // 2DO: I dont think this is needed anymore
    // private _clearCCFormData(): void {
    //     const div = document.getElementById('div_checkout_credit_entry');
    //     if (div) {
    //         const inputs = div.querySelectorAll('input');
    //         inputs.forEach(input => {
    //             input.value = '';
    //         });
    //         const selectElements = document.querySelectorAll("select") as NodeListOf<HTMLSelectElement>;
    //         selectElements.forEach(select => {
    //             select.selectedIndex = -1;
    //         });
    //     }
    //     sessionStorage.clear();
    // }

    // 2DO: Create US: Creating Billing Address Checkbox
    // Util.setElement("onchange", "chk_checkout_billing_add", () => { this._setDeliveryAddressAsBilling(); });
    // private _setDeliveryAddressAsBilling = (): void => {
    //     this._isDeliveryBilling = Util.getElementChecked("chk_checkout_billing_add");
    //     if (aOLO.Temp.Address && this._isDeliveryBilling) {
    //         //set the delivery address as billing address
    //         Util.setElement("value", "txt_checkout_credit_entry_billing_address", `${aOLO.Temp.Address.StreetNo} ${aOLO.Temp.Address.StreetName}${aOLO.Temp.Address.Address2 ? ' #' +
    //             aOLO.Temp.Address.Address2 : ''}, ${aOLO.Temp.Address.City}, ${aOLO.Temp.Address.State}`);
    //         Util.setElement("value", "txt_checkout_credit_entry_billing_zip", aOLO.Temp.Address.Zip);
    //     }
    //     else if (!this._isDeliveryBilling) {
    //         Util.setElement("value", "txt_checkout_credit_entry_billing_address", ``);
    //         Util.setElement("value", "txt_checkout_credit_entry_billing_zip", '');
    //     }
    // }

    // 2DO: Create US: Punchh Gift Card Logic
    // Util.setElement("onclick", "btn_checkout_import_digital_gift_card", () => { this._giftCardPasscodeDialog() });
    // private _giftCardPasscodeDialog = (): void => {
    //     DialogCreators.InputBox(Names("EnterPasscode"),
    //         false,
    //         null,
    //         null,
    //         aOLO,
    //         [{
    //             text: Names("Confirm"), close: true, callBack: (passcode) => {
    //                 new GiftCards(passcode);
    //             }

    //         }, {
    //             text: Names("Cancel"), close: true, callBack: null
    //         }], null, null);
    //     return;
    // }

    // 2DO: Create US: Punchh Redeem Rewards Logic
    // Util.setElement("onclick", "btn_checkout_redeem_currency_apply", () => { this._applyRedeemCurrency(); });
    // private _setRedeemCurrencyGUI = (): void => {
    //     if (!aOLOModules.LoyaltyProvider.hasBankedCurrency() || !aOLO.User?.LoyaltyData) {
    //         Util.hideElement("div_checkout_redeem_currency");
    //         return;
    //     }

    //     Util.hideElement("div_checkout_redeem_currency_applied");
    //     Util.showElement("div_checkout_redeem_currency");

    //     Util.setElement("innerText", "div_checkout_redeem_currency_available_amount", Util.formatMoney(aOLO.User.LoyaltyData.BankedCurrency || 0));
    //     this._setAppliedRedeemCurrencyGUI();
    // }

    // private _applyRedeemCurrency = async (): Promise<void> => {
    //     if (aOLO.Order.Coupons.find(x => x.CouponId == 10 && x.RewardId == null)) {
    //         DialogCreators.messageBoxOk(Names("RedeemRewardHasAlreadyBeenAppliedError"), this._context.getProperty("buttonHoverStyle"));
    //         return;
    //     }

    //     const amountLeftOrder = (aOLO.Order.SubTotal - aOLO.Order.Discount);
    //     const amount = Number(this._item.redeemCurrency);
    //     if (amount == 0) {
    //         DialogCreators.messageBoxOk(Names("PleaseEnterAmountGreaterThan").replace("{{x}}", Util.formatMoney(0)), this._context.getProperty("buttonHoverStyle"));
    //         return;
    //     } else if (amount > amountLeftOrder) {
    //         DialogCreators.messageBoxOk(Names("PleaseEnterAmountLessThanAmountDue").replace("{{x}}", Util.formatMoney(amountLeftOrder)), this._context.getProperty("buttonHoverStyle"));
    //         return;
    //     } else if (amount > (aOLO.User.LoyaltyData?.BankedCurrency || 0)) {
    //         DialogCreators.messageBoxOk(Names("PleaseEnterAmountWithinRangeFrom").replace("{{x}}", Util.formatMoney(0)).replace("{{y}}", Util.formatMoney(aOLO.User.LoyaltyData?.BankedCurrency || 0)), this._context.getProperty("buttonHoverStyle"));
    //         return;
    //     }

    //     await aOLOModules.LoyaltyProvider.applyBankedCurrency(amount);
    //     this._setAppliedRedeemCurrencyGUI();
    // }

    // private _setAppliedRedeemCurrencyGUI = (): void => {
    //     const amount = aOLOModules.LoyaltyProvider.getAppliedBankedCurrency();
    //     this._item.redeemCurrency = "";

    //     if (!aOLO.User.LoyaltyData?.BankedCurrency || amount == 0)
    //         return;

    //     Util.setElement("innerText", "div_checkout_redeem_currency_applied_amount", Util.formatMoney(amount));
    //     Util.setElement("innerText", "div_checkout_redeem_currency_remaining_amount", Util.formatMoney(aOLO.User.LoyaltyData.BankedCurrency - amount));

    //     Util.setElement("onclick", "btn_checkout_redeem_currency_remove", () => { this._removeBankedCurrency(); });
    //     Util.showElement("div_checkout_redeem_currency_applied");
    // }

    // private _removeBankedCurrency = async (): Promise<void> => {
    //     await aOLOModules.LoyaltyProvider.removeBankedCurrency();
    //     Util.hideElement("div_checkout_redeem_currency_applied");
    // }

    private readonly _isApplePayCapableBrowser = (): boolean => {
        const ua = navigator.userAgent;

        const isIOSDevice = /iPhone|iPad|iPod/.test(ua);
        const isMacUA = /Macintosh/.test(ua);
        const isIPadDesktopMode = isMacUA && navigator.maxTouchPoints > 1;

        if (isIOSDevice || isIPadDesktopMode) {
            // iOS (and iPadOS in "Request Desktop Website" mode) -- Apple's App Store rules
            // require every browser there to be a WebKit wrapper, so Safari, Chrome, Firefox,
            // and Edge all expose ApplePaySession the same way.
            const isChrome = /CriOS/.test(ua);
            const isFirefox = /FxiOS/.test(ua);
            const isEdge = /EdgiOS/.test(ua);
            const isSafari = /Safari/.test(ua) && !isChrome && !isFirefox && !isEdge && !/OPiOS|GSA/.test(ua);
            return isSafari || isChrome || isFirefox || isEdge;
        }

        if (isMacUA)
            return true;
        

        return false;
    };

    private readonly _isApplePayAvailable = (): boolean => {
        // The browser-engine allowlist below is only meaningful for the open web, where Apple
        // Pay must be gated to WebKit-based browsers. Our own wrapped iOS app's WKWebView often
        // carries a customized user agent that matches none of those patterns, so it's detected
        // the same explicit way Google Pay detects its wrapped Android app, instead of relying on UA sniffing.
        if (!this._isIOSNativeAppRequest() && !this._isApplePayCapableBrowser()) return false;

        const ApplePaySession = (window as any).ApplePaySession;
        if (!ApplePaySession) return false;
        try {
            ApplePaySession.canMakePayments();
        } catch (e) {
            console.warn("[ApplePay] canMakePayments error (HTTPS required):", e);
        }
        return true;
    }

    private _isHeartlandEcommerceProcessor(): boolean {
        return this._getEcommerceMerchant()?.ProcessorId === MerchantProcessor.Heartland;
    }

    private _isWalletPaymentOption(paymentOptionId: OnlinePaymentOption | null): boolean {
        return paymentOptionId === OnlinePaymentOption.APPLE_PAY || paymentOptionId === OnlinePaymentOption.GOOGLE_PAY;
    }

    /**
     * Heartland Portico can't run the $0 pre-validation a future-dated order needs, so Apple/Google Pay are only
     * offered there for the current business date. Checks both the picker (date changed, no time yet) and the order.
     */
    private _shouldHideWalletsForFutureDate(): boolean {
        if (!this._isHeartlandEcommerceProcessor())
            return false;

        const businessDate = Util.getBusinessDate(this._data);
        const isFutureDate = (date: Date | null): boolean => date !== null && Util.DateDiff("d", date, businessDate) > 0;
        const orderFutureDate = this._order.getProperty("FutureDate");

        return isFutureDate(this._modules.timeSection.getSelectedDate())
            || isFutureDate(orderFutureDate ? new Date(orderFutureDate) : null);
    }

    /**
     * Re-renders the payment options when the order date moves to/from a future date and that flips
     * whether Apple/Google Pay are offered, dropping a wallet selection that is no longer allowed.
     */
    private readonly _refreshWalletsForOrderDate = (): void => {
        if (Router.path !== Pages.CHECKOUT || !this._item.paymentOptions)
            return;

        const hideWallets = this._shouldHideWalletsForFutureDate();
        if (hideWallets === this._areWalletsHiddenForFutureDate)
            return;

        if (hideWallets && this._isWalletPaymentOption(this._item.paymentOptionId)) {
            this._item.paymentOptionId = null;
            Util.showElement("btn_checkout_place_order");
            Util.showElement("btn_checkout_place_order_mobile");
            Util.hideElement("btn_checkout_apple_pay_bottom");
            Util.hideElement("btn_checkout_apple_pay_bottom_mobile");
            Util.hideElement("div_checkout_google_pay_bottom");
            Util.hideElement("div_checkout_google_pay_bottom_mobile");
        }

        this._renderPaymentMethodsSection();
    }

    /**
     * Locks the date picker to the current date while Apple/Google Pay is selected on Heartland (see _shouldHideWalletsForFutureDate).
     * A zero amount due (e.g. fully covered by gift cards) hides the payment section, so nothing is charged to the wallet.
     */
    private _syncDatePickerLock(): void {
        const isLocked = this._order.getProperty("AmountDue") > 0
            && this._isWalletPaymentOption(this._item.paymentOptionId)
            && this._isHeartlandEcommerceProcessor();
        this._modules.timeSection.setDateLocked(isLocked);
    }

    private _createApplePayOption(radioGroupName: string): IPaymentOptionElement | null {
        if (!this._getEcommerceMerchant()?.isApplePay || !this._isApplePayAvailable() || this._areWalletsHiddenForFutureDate)
            return null;

        return {
            id: OnlinePaymentOption.APPLE_PAY,
            name: "Apple Pay",
            iconHtml: `<span class="pill-apple-pay-badge"></span>`,
            radioGroupName,
            isSelected: this._item.paymentOptionId === OnlinePaymentOption.APPLE_PAY,
            onSelect: () => {
                const btn = document.getElementById(`li_${radioGroupName}_${OnlinePaymentOption.APPLE_PAY}`) ?? undefined;
                const humanClicked = btn?.dataset.humanClick === 'true';
                this._paymentOptionOnClick(OnlinePaymentOption.APPLE_PAY, humanClicked);
            }
        };
    }

    private _applePayPaymentOption(): void {
        Util.hideElement("div_checkout_payment_existing");
        this._closeNewCardForm();
        this._item.creditCardInfoSaveToken = false;
        Util.hideElement("btn_checkout_place_order");
        Util.hideElement("btn_checkout_place_order_mobile");
        Util.showElement("btn_checkout_apple_pay_bottom");
        Util.showElement("btn_checkout_apple_pay_bottom_mobile");
        this._order.setProperty("PaymentType", PaymentType.CREDIT_CARD);
    }

    private _initiateApplePay(): void {
        const ApplePaySession = (window as any).ApplePaySession;
        if (!ApplePaySession) return;

        const amountDue = this._order.getProperty("AmountDue");
        const request = {
            countryCode: "US",
            currencyCode: "USD",
            supportedNetworks: ["visa", "masterCard", "amex", "discover"],
            merchantCapabilities: ["supports3DS"],
            total: {
                label: this._context.getProperty("brandInfo").Name || "Order Total",
                amount: amountDue.toFixed(2)
            }
        };

        const session = new ApplePaySession(3, request);

        session.onvalidatemerchant = async (event: any): Promise<void> => {
            try {
                const merchantSession = await this._validateApplePayMerchantAsync(event.validationURL);
                session.completeMerchantValidation(merchantSession);
            } catch {
                session.abort();
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("Error_ApplePayCannotStart"),
                    true
                );
            }
        };

        session.onpaymentauthorized = async (event: any): Promise<void> => {
            try {
                const paymentData = event.payment.token.paymentData;
                // Check the throttled slot before Apple Pay shows success; a full slot fails the sheet instead.
                if (!await this._modules.timeSection.confirmLimitedTimeReservationAsync()) {
                    session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
                    this._item.isSubmitting = false;
                    return;
                }

                this._item.applePayToken = typeof paymentData === "string" ? paymentData : JSON.stringify(paymentData);
                session.completePayment({ status: ApplePaySession.STATUS_SUCCESS });
                await this._placeOrderOnClickAsync();
            } catch {
                session.completePayment({ status: ApplePaySession.STATUS_FAILURE });
                this._item.isSubmitting = false;
            }
        };

        session.begin();
    }

    private readonly _validateApplePayMerchantAsync = async (validationURL: string): Promise<unknown> => {
        const response = await fetch("/api/v1/olo/apple-pay/validate-merchant", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ validationURL, storeKey: this._data.storeKey })
        });
        if (!response.ok)
            throw new Error("Apple Pay merchant validation failed");
        return response.json();
    }

    // ─── Google Pay ───────────────────────────────────────────────────────────────

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _googlePayClient: any = null;

    private readonly _googlePayBaseRequest = {
        apiVersion: 2,
        apiVersionMinor: 0
    };

    private readonly _googlePayAllowedCardNetworks = ["AMEX", "DISCOVER", "MASTERCARD", "VISA"];
    private readonly _googlePayAllowedAuthMethods = ["PAN_ONLY", "CRYPTOGRAM_3DS"];

    private _getGooglePaymentsClient(): unknown {
        if (this._googlePayClient) return this._googlePayClient;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const google = (window as any).google;
        if (!google?.payments?.api) return null;
        const env = this._context.getProperty("developmentMode") ? "TEST" : "PRODUCTION";
        this._googlePayClient = new google.payments.api.PaymentsClient({ environment: env });
        return this._googlePayClient;
    }

    private _createGooglePayOption(radioGroupName: string): IPaymentOptionElement | null {
        if (this._isIOSNativeAppRequest())
            return null;
        if (!this._getEcommerceMerchant()?.isGooglePay || this._areWalletsHiddenForFutureDate)
            return null;

        return {
            id: OnlinePaymentOption.GOOGLE_PAY,
            name: "Google Pay",
            iconHtml: `<span class="pill-google-pay-badge"></span>`,
            radioGroupName,
            isSelected: this._item.paymentOptionId === OnlinePaymentOption.GOOGLE_PAY,
            onSelect: () => {
                const btn = document.getElementById(`li_${radioGroupName}_${OnlinePaymentOption.GOOGLE_PAY}`) ?? undefined;
                const humanClicked = btn?.dataset.humanClick === "true";
                this._paymentOptionOnClick(OnlinePaymentOption.GOOGLE_PAY, humanClicked);
            }
        };
    }

    private _googlePayPaymentOption(): void {
        Util.hideElement("div_checkout_payment_existing");
        this._closeNewCardForm();
        this._item.creditCardInfoSaveToken = false;
        Util.hideElement("btn_checkout_place_order");
        Util.hideElement("btn_checkout_place_order_mobile");
        Util.hideElement("btn_checkout_apple_pay_bottom");
        Util.hideElement("btn_checkout_apple_pay_bottom_mobile");
        Util.showElement("div_checkout_google_pay_bottom");
        Util.showElement("div_checkout_google_pay_bottom_mobile");
        // Inject after container is visible — buttonSizeMode:"fill" requires a visible parent to compute layout
        if (this._getGooglePaymentsClient()) {
            void this._injectGooglePayBottomButtons();
        } else {
            // SDK not yet loaded; inject once the script fires its load event
            const script = document.querySelector<HTMLScriptElement>('script[src*="pay.google.com"]');
            if (script)
                script.addEventListener('load', () => { void this._injectGooglePayBottomButtons(); }, { once: true });
        }
        this._order.setProperty("PaymentType", PaymentType.CREDIT_CARD);
    }

    private _loadGooglePayScript(): void {
        if (this._isIOSNativeAppRequest())
            return;
        if ((window as any).google?.payments?.api) {
            void this._injectGooglePayButtons();
            return;
        }
        const existing = document.querySelector<HTMLScriptElement>('script[src*="pay.google.com"]');
        const onLoad = () => { void this._injectGooglePayButtons(); };
        if (existing)
            existing.addEventListener('load', onLoad, { once: true });
        else {
            const script = document.createElement('script');
            script.src = 'https://pay.google.com/gp/p/js/pay.js';
            script.addEventListener('load', onLoad, { once: true });
            document.head.appendChild(script);
        }
    }

     private _googlePayReadyPromise: Promise<boolean> | null = null;

    private _isGooglePayReadyAsync(paymentsClient: any): Promise<boolean> { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (this._isNativeWrappedAppRequest()) return Promise.resolve(true);
        if (this._googlePayReadyPromise) return this._googlePayReadyPromise;
        const isReadyToPayRequest = {
            ...this._googlePayBaseRequest,
            allowedPaymentMethods: [{
                type: "CARD",
                parameters: {
                    allowedAuthMethods: this._googlePayAllowedAuthMethods,
                    allowedCardNetworks: this._googlePayAllowedCardNetworks
                }
            }]
        };
        const readyPromise: Promise<boolean> = paymentsClient.isReadyToPay(isReadyToPayRequest)
            .then((response: any) => response?.result === true) // eslint-disable-line @typescript-eslint/no-explicit-any
            .catch((err: unknown) => {
                console.warn("[GooglePay] isReadyToPay failed — hiding the Google Pay button.", err);
                return false;
            })
            .then((isReady: boolean) => {
                // Don't cache a not-ready result: this check also fires eagerly on page load
                // (for the unused top button container), and on mobile networks it can transiently
                // report false before Google Pay eligibility has resolved. Caching that forever
                // would permanently hide the bottom button even once the device is actually ready.
                if (!isReady) this._googlePayReadyPromise = null;
                return isReady;
            });
        this._googlePayReadyPromise = readyPromise;
        return readyPromise;
    }

    private async _injectGooglePayButtons(): Promise<void> {
        const paymentsClient = this._getGooglePaymentsClient() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (!paymentsClient) return;
        if (!(await this._isGooglePayReadyAsync(paymentsClient))) return;
        const onClick = () => { void this._initiateGooglePayAsync(); };
        const container = document.getElementById("btn_checkout_google_pay");
        if (container?.children.length === 0)
            container.appendChild(paymentsClient.createButton({ onClick, buttonType: "buy", buttonSizeMode: "fill", buttonLocale: "en" }));
    }

    private async _injectGooglePayBottomButtons(): Promise<void> {
        const paymentsClient = this._getGooglePaymentsClient() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (!paymentsClient) return;
        if (!(await this._isGooglePayReadyAsync(paymentsClient))) return;
        const onClick = () => { void this._placeOrderOnClickAsync(); };
        for (const containerId of ["div_checkout_google_pay_bottom", "div_checkout_google_pay_bottom_mobile"]) {
            const container = document.getElementById(containerId);
            if (!container) continue;
            container.innerHTML = "";
            container.appendChild(paymentsClient.createButton({ onClick, buttonType: "buy", buttonSizeMode: "fill", buttonLocale: "en", buttonRadius: 10 }));
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private _getGooglePayTokenizationSpecification(): any {
        const merchant = this._getEcommerceMerchant();
        const processorId = merchant?.ProcessorId;
        const brandInfo = this._context.getProperty("brandInfo");

        // WorldPay: decrypt the token ourselves server-side, so Google encrypts directly for our own EC key pair
        if (processorId === MerchantProcessor.WorldPayTriPOS) {
            return {
                type: "DIRECT",
                parameters: {
                    protocolVersion: "ECv2",
                    publicKey: brandInfo?.GooglePayPublicKey ?? ""
                }
            };
        }

        const gateway = processorId === MerchantProcessor.Heartland ? "globalpayments" : "";
        return {
            type: "PAYMENT_GATEWAY",
            parameters: {
                gateway,
                gatewayMerchantId: merchant?.gatewayMerchantId ?? ""
            }
        };
    }

    private readonly _loadNativeGooglePayDataAsync = async (paymentDataRequest: unknown): Promise<any> => { // eslint-disable-line @typescript-eslint/no-explicit-any
        if (typeof startNativeGooglePay === "function") {
            return awaitNativeOnce<any>({ // eslint-disable-line @typescript-eslint/no-explicit-any
                name: "NativeGooglePay",
                setResolver: (r) => { globalThis.pendingGooglePayResolve = r; },
                clearResolver: () => { globalThis.pendingGooglePayResolve = null; },
                invokeNative: () => startNativeGooglePay!(JSON.stringify(paymentDataRequest)),
                timeoutValue: null,
                timeoutMs: 60000 // native Google Pay UI needs time for user interaction
            });
        }

        const jsBridge = (globalThis as any).jsBridge; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (jsBridge && typeof jsBridge.invokeAction === "function") {
            const fallbackTimedOut = Symbol("jsBridgeFallbackTimeout");
            const result = await awaitNativeOnce<any>({ // eslint-disable-line @typescript-eslint/no-explicit-any
                name: "NativeGooglePayViaJsBridge",
                setResolver: (r) => { globalThis.pendingGooglePayResolve = r; },
                clearResolver: () => { globalThis.pendingGooglePayResolve = null; },
                invokeNative: () => jsBridge.invokeAction(JSON.stringify({ action: "startNativeGooglePay", data: paymentDataRequest })),
                timeoutValue: fallbackTimedOut,
                timeoutMs: 8000
            });
            if (result !== fallbackTimedOut)
                return result;
            console.warn("[GooglePay] window.jsBridge.invokeAction fallback timed out with no native response -- action likely unrecognized.");
        }

        const jsBridgeType = typeof jsBridge;
        let jsBridgeMethods = "n/a";
        if (jsBridge) {
            try {
                jsBridgeMethods = JSON.stringify(Object.getOwnPropertyNames(jsBridge));
            } catch (ex) {
                jsBridgeMethods = `error enumerating: ${(ex as Error)?.message}`;
            }
        }
        console.warn(
            "[GooglePay] startNativeGooglePay bridge not available. " +
            `userAgent=${window.navigator.userAgent} isAppView=${Util.isAppView()} isAndroid=${Util.isAndroid()} isiOS=${Util.isiOS()} ` +
            `jsBridgeType=${jsBridgeType} jsBridgeMethods=${jsBridgeMethods}`
        );
        throw new Error("NativeGooglePayBridgeUnavailable");
    };

    private _isMobileAppRequest(): boolean {
        const liveParam = Util.getQueryParamCaseInsensitive(window.location.search, "Mobile");
        if (liveParam !== null)
            return liveParam.toLowerCase() === "true";
        return sessionStorage.getItem("mobileAppRequest") === "true";
    }


    private _isNativeWrappedAppRequest(): boolean {
        return this._isMobileAppRequest() && Util.isAndroid();
    }

    private _isIOSNativeAppRequest(): boolean {
        return this._isMobileAppRequest() && Util.isiOS();
    }

    private readonly _initiateGooglePayAsync = async (): Promise<void> => {
        // Native bridge (startNativeGooglePay/setNativeGooglePayResult) only applies to the
        // wrapped Android app; iOS and the web fall through to the web SDK's loadPaymentData().
        const isNativeWrappedApp = this._isNativeWrappedAppRequest();
        const paymentsClient = isNativeWrappedApp ? null : this._getGooglePaymentsClient() as any; // eslint-disable-line @typescript-eslint/no-explicit-any
        if (!isNativeWrappedApp && !paymentsClient) {
            console.warn("[GooglePay] no paymentsClient — Google Pay SDK script not loaded yet");
            return;
        }

        try {
            const brandInfo = this._context.getProperty("brandInfo");
            const amountDue = this._order.getProperty("AmountDue");

            const paymentDataRequest = {
                ...this._googlePayBaseRequest,
                allowedPaymentMethods: [{
                    type: "CARD",
                    parameters: {
                        allowedAuthMethods: this._googlePayAllowedAuthMethods,
                        allowedCardNetworks: this._googlePayAllowedCardNetworks
                    },
                    tokenizationSpecification: this._getGooglePayTokenizationSpecification()
                }],
                merchantInfo: {
                    merchantId: brandInfo?.GooglePayMerchantId ?? "",
                    merchantName: brandInfo?.Name ?? "Order Total"
                },
                transactionInfo: {
                    totalPriceStatus: "FINAL",
                    totalPrice: (amountDue ?? 0).toFixed(2),
                    currencyCode: "USD",
                    countryCode: "US"
                }
            };

            const paymentData = isNativeWrappedApp
                ? await this._loadNativeGooglePayDataAsync(paymentDataRequest)
                : await paymentsClient.loadPaymentData(paymentDataRequest);

            if (!paymentData) {
                // Native side reported user cancellation/failure — same as the web CANCELED case below.
                this._item.isSubmitting = false;
                return;
            }

            // tokenizationData.token is already a JSON-stringified string per Google Pay's API — do not re-stringify it.
            const rawGooglePayToken = paymentData.paymentMethodData.tokenizationData.token;
            this._item.googlePayToken = rawGooglePayToken;
            await this._placeOrderOnClickAsync();
        } catch (err: unknown) {
            // User cancelled — statusCode "CANCELED" is expected, suppress it
            const statusCode = (err as any)?.statusCode; // eslint-disable-line @typescript-eslint/no-explicit-any
            if (statusCode !== "CANCELED") {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayer,
                    this._localizationService.names("Error_GooglePayCannotComplete"),
                    true
                );
            }
            this._item.isSubmitting = false;
        }
    }
}